/**
 * Results of an exploration: the measured samples, plotted and tabulated.
 *
 * One component serves two routes through `pageMode`:
 *   "iteration" - the results of the iteration just measured, seen while the
 *                 exploration is running;
 *   "analyse"   - the finished exploration, where the generation data is loaded
 *                 only when its tab is opened and the export is prepared on
 *                 request.
 *
 * Colours shown on this page are computed from the measured L*a*b*, never from
 * the stored RGB.
 */
import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Stack,
    Slider,
    Box,
    Tabs,
    Tab,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    Card,
    CardContent,
    Chip,
    Avatar,
    TextField,
    CircularProgress,
    Tooltip as MuiTooltip,
} from "@mui/material";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DesignPointsErrorAlert from "../../components/DesignPointsErrorAlert";
import NavBarBottom from "../../components/NavBarBottom";
import { useExplorationParams } from "../../helper/entityRoutes";
import { EXPLORATION_LIST_ROUTE } from "../../helper/explorationRoutes";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import { PlatesView } from "./ExplorationPlates";
import { PhysicalPatchGroups, MemberOriginColumns, originLabelOf } from "../../components/MemberPatchPicker";
import CaptureImage from "../../components/CaptureImage";
import { downloadBlob } from "../../helper/markingPreviewWindow";
import { formatTo3, formatParameterLabel } from "../../components/SamplePointDetails";
import { labToDisplayRgb } from "../../helper/colorConversion";
import ExplorationConfigsPanel from "../../components/ExplorationConfigsPanel";
import BinBarChart from "../../components/BinBarChart"
import MyScatterChart from "../../components/MyScatterChart"
import MyScatterChartWhiteBlack from "../../components/MyScatterChartWhiteBlack";
import ValidSamplesGamut from "../../components/ValidSamplesGamut";
import axios from "axios";
import {
    EXPLORATION_FLOW_STEPS,
    populationLabelForPhase,
} from "../../helper/colorExplorationParameters";

const CHART_WIDTH = 520;
const CHART_HEIGHT = 380;
const CHART_MARGIN = { top: 12, right: 16, left: 12, bottom: 36 };

// How the first population came about (Exploration.start_mode).
const START_MODE_LABELS = { random: "Random population", warm_start: "Warm start", manual: "Manual samples" };

// The selection's basic filters, as the backend applies them by default
// (api/exploration/valid/samples): width in um, marking time in s/cm², line
// count (empty = any), chromaticity C*ab, L*.
export const VALID_SAMPLES_DEFAULTS = {
    maximumWidthUm: 60, maximumTimePerCm2: 140, lineCount: "", chromaticityThreshold: 5, lightnessThreshold: 60,
};

const isBlank = (text) => text === "" || text === null || text === undefined;

/** A typed threshold: a finite number of 0 or more, or null. */
export function parseThreshold(text) {
    if (isBlank(text)) return null;
    const value = Number(text);
    return Number.isFinite(value) && value >= 0 ? value : null;
}

/** A threshold that may be left empty (no limit): ``{ value, ok }`` with
 *  value null when empty, ok false when typed but not a number of 0 or more. */
export function parseOptionalThreshold(text) {
    if (isBlank(text)) return { value: null, ok: true };
    const value = parseThreshold(text);
    return { value, ok: value !== null };
}

/** A line count: empty means any; otherwise a whole number of 1 or more. */
export function parseLineCount(text) {
    if (isBlank(text)) return { value: null, ok: true };
    const value = Number(text);
    const ok = Number.isInteger(value) && value >= 1;
    return { value: ok ? value : null, ok };
}
const BIN_CHART_MARGIN = { top: 12, right: 20, left: 12, bottom: 52 };
const RESULT_PANEL_MAX_W = CHART_WIDTH + 48;
const SLIDER_ROW_MIN_HEIGHT = 52;
// Dropdown values are the internal collection roles; the UI names the "combined" role "Generated".
const COLLECTION_ROLE_LABELS = { input: "Input", combined: "Generated", output: "Output" };
const collectionRoleLabel = (role) => COLLECTION_ROLE_LABELS[role] || role;
const roundTo3 = (value) => Math.round(Number(value) * 1000) / 1000;
const floorTo3 = (value) => Math.floor(Number(value) * 1000) / 1000;
const ceilTo3 = (value) => Math.ceil(Number(value) * 1000) / 1000;
const computeChromaticity = (aValue, bValue) => {
    const a = Number(aValue);
    const b = Number(bValue);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.sqrt((a * a) + (b * b));
};
const resultPanelSx = {
    width: "100%",
    maxWidth: RESULT_PANEL_MAX_W,
    mx: "auto",
    p: 2.5,
    boxSizing: "border-box",
    borderRadius: 2,
    bgcolor: "background.paper",
    boxShadow: "0 1px 4px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
};

const scopeToggleSx = {
    mt: 1,
    alignSelf: "center",
    bgcolor: "action.hover",
    p: 0.5,
    borderRadius: 2,
    "& .MuiToggleButton-root": {
        px: 2.5,
        py: 0.85,
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.875rem",
        border: "none",
        borderRadius: "10px !important",
        color: "text.secondary",
        "&.Mui-selected": {
            bgcolor: "background.paper",
            boxShadow: 2,
            color: "primary.main",
        },
    },
};

function ColorExplorationColorSummary({ pageMode = "iteration" }) {

    const location = useLocation();
    const [iterationNumber, setIterationNumber] = useState(0);
    const [widthData, setWidthData] = useState([]);
    const [colorData, setColorData] = useState([]);
    const [widthResults, setWidthResults] = useState([]);
    const [colorResults, setColorResults] = useState([]);
    const [whiteBlackColorResults, setWhiteBlackColorResults] = useState([]);
    const [selectedValueWidth, setSelectedValueWidth] = useState('current');
    const [selectedValueColor, setSelectedValueColor] = useState('current');
    const [selectedValueWhiteBlackColor, setSelectedValueWhiteBlackColor] = useState('current');
    const [blackWhitecolorData, setBlackWhiteColorData] = useState([]);
    const [blackWhiteExploration, setBlackWhiteExploration] = useState(false);
    const [widthSliderValue, setWidthSliderValue] = useState(0);
    const [colorSliderValue, setColorSliderValue] = useState(0);
    const [whiteBlackColorSliderValue, setWhiteBlackColorSliderValue] = useState(0);
    const [showWidthSlider, setShowWidthSlider] = useState(false);
    const [showColorSlider, setShowColorSlider] = useState(false);
    const [showBlackWhiteSlider, setShowBlackWhiteSlider] = useState(false);
    const [resultsTab, setResultsTab] = useState(0);
    const [explorationData, setExplorationData] = useState(null);
    const [objectiveName, setObjectiveName] = useState("");
    const [analysisIteration, setAnalysisIteration] = useState(0);
    const [iterationRecords, setIterationRecords] = useState([]);
    const [collectionSampleMaps, setCollectionSampleMaps] = useState([]);
    const [generationLoadError, setGenerationLoadError] = useState("");
    const [collectionsData, setCollectionsData] = useState([]);
    const [laserDesignParameters, setLaserDesignParameters] = useState([]);
    const [laserDesignParameterTypes, setLaserDesignParameterTypes] = useState([]);
    const [colorMeasurementSets, setColorMeasurementSets] = useState([]);
    const [widthMeasurementSets, setWidthMeasurementSets] = useState([]);
    const [measurementRgb, setMeasurementRgb] = useState([]);
    const [measurementCielab, setMeasurementCielab] = useState([]);
    const [measurementCiexyz, setMeasurementCiexyz] = useState([]);
    const [measurementPixel, setMeasurementPixel] = useState([]);
    const [measurementMillimeter, setMeasurementMillimeter] = useState([]);
    const [sourcePatches, setSourcePatches] = useState([]);
    const [sourcePatchColors, setSourcePatchColors] = useState([]);
    const [samplePatchImageUrls, setSamplePatchImageUrls] = useState({});
    const [selectedCollectionRole, setSelectedCollectionRole] = useState("input");
    // Whether the selected collection ever reached the laser, and what was
    // photographed of it. Answered by the backend from the collection's patch
    // groups -- the role alone cannot tell us (a generated collection is never
    // marked, an output collection only becomes marked as the next iteration's
    // input).
    const [collectionMarking, setCollectionMarking] = useState(null);
    const [isLoadingCollectionMarking, setIsLoadingCollectionMarking] = useState(false);
    // Patch grids (one per marking of the browsed collection), keyed by
    // patch_group_id. They are the click-to-pick member selector.
    const [markingPatchGrids, setMarkingPatchGrids] = useState({});
    const [isLoadingMarkingPatchGrids, setIsLoadingMarkingPatchGrids] = useState(false);
    // The exploration is the URL's (/colorexplorations/<id>/summary|analyse).
    const { explorationId, stepRoute } = useExplorationParams();
    // Phase (P / Q) of the running iteration, for the sidebar's population label.
    const [iterationPhase, setIterationPhase] = useState(null);
    const [selectedMemberId, setSelectedMemberId] = useState("");
    const [generationCollectionRole, setGenerationCollectionRole] = useState("input");
    const [generationSelectedMemberId, setGenerationSelectedMemberId] = useState("");
    const [evolutionMetric, setEvolutionMetric] = useState("width");
    const [widthGrowthMin, setWidthGrowthMin] = useState(0);
    const [widthGrowthMax, setWidthGrowthMax] = useState(0);
    // The "Valid samples" tab (ORC-1163): per-iteration counts of the measured
    // samples that pass the basic selection filters, for the thresholds typed
    // here (the selection's defaults to begin with). Typed as text so a value
    // is never clamped behind the user's back; an invalid one is refused.
    const [validSamplesData, setValidSamplesData] = useState(null);
    const [validSamplesError, setValidSamplesError] = useState("");
    const [validMaxWidthText, setValidMaxWidthText] = useState(String(VALID_SAMPLES_DEFAULTS.maximumWidthUm));
    const [validTimeText, setValidTimeText] = useState(String(VALID_SAMPLES_DEFAULTS.maximumTimePerCm2));
    const [validLineCountText, setValidLineCountText] = useState(VALID_SAMPLES_DEFAULTS.lineCount);
    const [validChromaticityText, setValidChromaticityText] = useState(String(VALID_SAMPLES_DEFAULTS.chromaticityThreshold));
    const [validLightnessText, setValidLightnessText] = useState(String(VALID_SAMPLES_DEFAULTS.lightnessThreshold));
    const [validGamutIteration, setValidGamutIteration] = useState(null);
    const [isAnalyseInitialDataLoading, setIsAnalyseInitialDataLoading] = useState(pageMode === "analyse");
    const [isGenerationDataLoading, setIsGenerationDataLoading] = useState(false);
    const [hasLoadedGenerationData, setHasLoadedGenerationData] = useState(false);
    const [isMemberDataLoading, setIsMemberDataLoading] = useState(false);
    const [isPreparingExplorationExport, setIsPreparingExplorationExport] = useState(false);
    const [isPreparingCollectionExport, setIsPreparingCollectionExport] = useState(false);
    // The next generation (iteration mode): bred by the genetic algorithm, or
    // the population uploaded here - an injected generation, posted as
    // ``points`` of api/next/iteration/data when Next is pressed. The chosen
    // file is checked by the server at once (api/exploration/injected-population/check,
    // the same rules Next applies), so a faulty file is refused where it is
    // chosen and Next stays disabled until a valid one is loaded.
    const [nextGenerationSource, setNextGenerationSource] = useState("optimizer");
    // { name, points } of the chosen file, once it parsed as JSON.
    const [injectedFile, setInjectedFile] = useState(null);
    // The server's verdict on it: { status: "checking" } | { status: "ok",
    // pointCount, ignoredKeys } | { status: "refused", error }.
    const [injectedCheck, setInjectedCheck] = useState(null);
    const [nextGenerationBusy, setNextGenerationBusy] = useState(false);
    const [nextGenerationError, setNextGenerationError] = useState(null);
    const injectedFileInputRef = useRef(null);
    // A file chosen while an older check is in flight must not be judged by
    // that older answer.
    const injectedCheckSeq = useRef(0);

    const navigate = useNavigate();

    const handleWidthScopeChange = (_event, newValue) => {
        if (newValue === null) return;
        setSelectedValueWidth(newValue);
        if (newValue === "current") {
            setShowWidthSlider(false);
            setWidthResults(getClusterWidthsForIteration(widthData, iterationNumber));
        } else {
            setWidthSliderValue(iterationNumber);
            setShowWidthSlider(true);
            setWidthResults(getClusterWidthsForCumulativeIteration(widthData, iterationNumber));
        }
    };

    const handleColorScopeChange = (_event, newValue) => {
        if (newValue === null) return;
        setSelectedValueColor(newValue);
        if (newValue === "current") {
            setShowColorSlider(false);
            setColorResults(getColorResultsForIteration(colorData, iterationNumber));
        } else {
            setShowColorSlider(true);
            setColorSliderValue(iterationNumber);
            setColorResults(getColorResultsForCumulativeIteration(colorData, iterationNumber));
        }
    };

    const handleBlackWhiteScopeChange = (_event, newValue) => {
        if (newValue === null) return;
        setSelectedValueWhiteBlackColor(newValue);
        if (newValue === "current") {
            setShowBlackWhiteSlider(false);
            setWhiteBlackColorResults(getColorResultsForIteration(blackWhitecolorData, iterationNumber));
        } else {
            setShowBlackWhiteSlider(true);
            setWhiteBlackColorSliderValue(iterationNumber);
            setWhiteBlackColorResults(getColorResultsForCumulativeIteration(blackWhitecolorData, iterationNumber));
        }
    };

    const handleWidthSliderChange = (event, newValue) => {
        setWidthSliderValue(newValue);
        updateWidthResults(newValue);
    };

    const handleColorSliderChange = (event, newValue) => {
        setColorSliderValue(newValue);
        updateColorResults(newValue);
    };

    const handleBlackWhiteColorSliderChange = (event, newValue) => {
        setWhiteBlackColorSliderValue(newValue);
        updateBlackWhiteColorResults(newValue);
    };

    const updateWidthResults = (iteration) => {
        const currentWidthResults = getClusterWidthsForCumulativeIteration(widthData, iteration);
        if (currentWidthResults.length > 0) {
            setWidthResults(currentWidthResults);
        }
    };

    const updateColorResults = (iteration) => {
        const currentColorResults = getColorResultsForCumulativeIteration(colorData, iteration);
        if (currentColorResults.length > 0) {
            setColorResults(currentColorResults);
        }
    };

    const updateBlackWhiteColorResults = (iteration) => {
        const currentWhiteBlackColorResults = getColorResultsForCumulativeIteration(blackWhitecolorData, iteration);
        if (currentWhiteBlackColorResults.length > 0) {
            setWhiteBlackColorResults(currentWhiteBlackColorResults);
        }
    };

    function getClusterWidthsForCumulativeIteration(dataList, iterationNumber) {
        const cap = Number(iterationNumber);
        const filteredList = dataList.filter(
            (item) =>
                Number(item.iteration_number) <= cap && item.cluster_width > 0
        );
        const clusterWidthList = filteredList.map(item => item.cluster_width);
        return clusterWidthList;
    }

    function getClusterWidthsForIteration(dataList, iterationNumber) {
        const filteredList = dataList.filter(
            (item) =>
                Number(item.iteration_number) === Number(iterationNumber) &&
                item.cluster_width != -1
        );
        const clusterWidthList = filteredList.map(item => item.cluster_width);
        return clusterWidthList;
    }

    function getColorResultsForCumulativeIteration(dataList, iterationNumber) {
        const cap = Number(iterationNumber);
        return dataList.filter(
            (item) => Number(item.iteration_number) <= cap
        );
    }

    function getColorResultsForIteration(dataList, iterationNumber) {
        const target = Number(iterationNumber);
        return dataList.filter(
            (item) => Number(item.iteration_number) === target
        );
    }

    const getCumulativeWidthData = async (displayIteration) => {
        const request_data = {
            "explorationId": explorationId,
            "cumulative": true
        }
        const response = await axios.post('api/exploration/measurement/width/results', request_data);
        setWidthData(response.data.width);
        let iter =
            displayIteration !== undefined && displayIteration !== null
                ? displayIteration
                : iterationNumber;
        const dataMax = maxIterationInData(response.data.width);
        if (dataMax !== null && dataMax > iter) {
            iter = dataMax;
            setIterationNumber(dataMax);
        }
        const apiWidthData = getClusterWidthsForIteration(response.data.width, iter);
        setWidthResults(apiWidthData);
    }

    // The backend's "current iteration" is the PENDING one, but its width and
    // color measurements are persisted while it is still pending (the
    // iteration only completes when the NEXT one is generated). Deriving the
    // display cap as current-1 therefore hides the freshest measured
    // iteration; the datasets themselves are the authority.
    const maxIterationInData = (dataList) => {
        if (!Array.isArray(dataList) || dataList.length === 0) return null;
        return dataList.reduce(
            (max, item) => Math.max(max, Number(item.iteration_number) || 0),
            0
        );
    };

    const getIterationNumber = async () => {
        const request_data = {
            "explorationId": explorationId
        }
        const response = await axios.post('api/exploration/iteration/number', request_data);
        setIterationPhase(response.data.iterationPhase || null);
        var iter = response.data.iterationNumber;
        if (iter == 0) {
            iter = 0
        }
        else {
            iter = iter - 1;
        }
        setIterationNumber(iter);
        return iter;
    }

    const getCumulativeColorData = async (displayIteration) => {
        const request_data = {
            "explorationId": explorationId,
            "cumulative": true
        }
        const response = await axios.post('api/exploration/measurement/color/results', request_data);
        // setCumulativeColorData(response.data.color);
        setColorData(response.data.color);
        let iter =
            displayIteration !== undefined && displayIteration !== null
                ? displayIteration
                : iterationNumber;
        const dataMax = maxIterationInData(response.data.color);
        if (dataMax !== null && dataMax > iter) {
            iter = dataMax;
            setIterationNumber(dataMax);
        }
        const apiColorData = getColorResultsForIteration(response.data.color, iter);
        setColorResults(apiColorData);
    }

    const getWhiteBlackCumulativeColorData = async (displayIteration) => {
        const request_data = {
            "explorationId": explorationId,
            "cumulative": true
        }
        const response = await axios.post('api/exploration/measurement/color/whiteblack/results', request_data);
        // setBlackWhiteCumulativeColorData(response.data.color);
        setBlackWhiteColorData(response.data.color);
        let iter =
            displayIteration !== undefined && displayIteration !== null
                ? displayIteration
                : iterationNumber;
        const dataMax = maxIterationInData(response.data.color);
        if (dataMax !== null && dataMax > iter) {
            iter = dataMax;
            setIterationNumber(dataMax);
        }
        const apiColorData = getColorResultsForIteration(response.data.color, iter);
        setWhiteBlackColorResults(apiColorData);
    }


    const getValidSamplesData = async (thresholds) => {
        try {
            const response = await axios.post("api/exploration/valid/samples", {
                explorationId,
                maximumWidthUm: thresholds.maximumWidthUm,
                maximumTimePerCm2: thresholds.maximumTimePerCm2,
                lineCount: thresholds.lineCount,
                chromaticityThreshold: thresholds.chromaticityThreshold,
                lightnessThreshold: thresholds.lightnessThreshold,
            });
            setValidSamplesData(response.data);
            setValidSamplesError("");
        } catch (error) {
            setValidSamplesError(error?.response?.data?.error || "Loading the valid samples failed.");
        }
    };

    const getExplorationObjective = async (displayIteration) => {
        const response = await axios.get('api/color/explorer/exploration/?id=' + explorationId);
        const explorationRow = response.data?.[0] || null;
        setExplorationData(explorationRow);
        if (!explorationRow?.exploration_objective) {
            setObjectiveName("");
            setBlackWhiteExploration(false);
            return;
        }
        try {
            const objectiveResponse = await axios.get(
                'api/color/explorer/exploration/objective/?id=' + explorationRow.exploration_objective
            );
            const objective = objectiveResponse.data?.[0]?.name || "";
            setObjectiveName(objective);
            const isBlackOrWhiteFlow = objective !== "chromatic_exploration";
            setBlackWhiteExploration(isBlackOrWhiteFlow);
            if (isBlackOrWhiteFlow) {
                await getWhiteBlackCumulativeColorData(displayIteration);
            }
        } catch (error) {
            console.error('Error:', error);
            setObjectiveName("");
            setBlackWhiteExploration(false);
        }
    }

    const normalizeListPayload = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.results)) return payload.results;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    };

    const getGenerationDataForExport = async () => {
        if (!explorationId) return null;
        const requestConfig = { timeout: 20000 };

        const [iterationsResponse, mapsResponse] = await Promise.all([
            axios.get("api/color/explorer/iteration/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/collection/sample/map/?exploration=" + explorationId, requestConfig),
        ]);
        const [
            designParamsResponse,
            designParamTypesResponse,
            colorMeasurementSetResponse,
            widthMeasurementSetResponse,
            rgbMeasurementResponse,
            cielabMeasurementResponse,
            ciexyzMeasurementResponse,
            pixelMeasurementResponse,
            millimeterMeasurementResponse,
        ] = await Promise.all([
            axios.get("api/color/explorer/laser/design/parameter/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/laserdesign/parameter/enum/", requestConfig),
            axios.get("api/color/explorer/measurement/color/set/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/measurement/width/set/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/measurement/rgb/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/measurement/cielab/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/measurement/ciexyz/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/measurement/pixel/?exploration=" + explorationId, requestConfig),
            axios.get("api/color/explorer/measurement/millimeter/?exploration=" + explorationId, requestConfig),
        ]);

        const allIterations = normalizeListPayload(iterationsResponse.data);
        const filteredIterations = allIterations
            .filter((iteration) => String(iteration?.exploration) === explorationId)
            .sort((a, b) => Number(a?.iteration_number) - Number(b?.iteration_number));

        const relevantCollectionIds = new Set();
        filteredIterations.forEach((iteration) => {
            if (iteration?.input_collection) relevantCollectionIds.add(String(iteration.input_collection));
            if (iteration?.generated_collection) relevantCollectionIds.add(String(iteration.generated_collection));
            if (iteration?.output_collection) relevantCollectionIds.add(String(iteration.output_collection));
        });

        const allMaps = normalizeListPayload(mapsResponse.data);
        const filteredMaps = allMaps.filter((entry) =>
            relevantCollectionIds.has(String(entry?.collection))
        );

        return {
            collectionSampleMaps: filteredMaps,
            laserDesignParameters: normalizeListPayload(designParamsResponse.data),
            laserDesignParameterTypes: normalizeListPayload(designParamTypesResponse.data),
            colorMeasurementSets: normalizeListPayload(colorMeasurementSetResponse.data),
            widthMeasurementSets: normalizeListPayload(widthMeasurementSetResponse.data),
            measurementRgb: normalizeListPayload(rgbMeasurementResponse.data),
            measurementCielab: normalizeListPayload(cielabMeasurementResponse.data),
            measurementCiexyz: normalizeListPayload(ciexyzMeasurementResponse.data),
            measurementPixel: normalizeListPayload(pixelMeasurementResponse.data),
            measurementMillimeter: normalizeListPayload(millimeterMeasurementResponse.data),
        };
    };

    const getGenerationData = async () => {
        try {
            setGenerationLoadError("");
            if (!explorationId) {
                setIterationRecords([]);
                setCollectionSampleMaps([]);
                return null;
            }

            const [iterationsResponse, mapsResponse] = await Promise.all([
                axios.get("api/color/explorer/iteration/?exploration=" + explorationId),
                axios.get("api/color/explorer/collection/sample/map/?exploration=" + explorationId),
            ]);
            const [collectionsResponse, designParamTypesResponse] = await Promise.all([
                axios.get("api/color/explorer/collection/?exploration=" + explorationId),
                axios.get("api/color/explorer/laserdesign/parameter/enum/"),
            ]);

            const allIterations = normalizeListPayload(iterationsResponse.data);
            const filteredIterations = allIterations
                .filter((iteration) => String(iteration?.exploration) === explorationId)
                .sort((a, b) => Number(a?.iteration_number) - Number(b?.iteration_number));

            const relevantCollectionIds = new Set();
            filteredIterations.forEach((iteration) => {
                if (iteration?.input_collection) relevantCollectionIds.add(String(iteration.input_collection));
                if (iteration?.generated_collection) relevantCollectionIds.add(String(iteration.generated_collection));
                if (iteration?.output_collection) relevantCollectionIds.add(String(iteration.output_collection));
            });

            const allMaps = normalizeListPayload(mapsResponse.data);
            const filteredMaps = allMaps.filter((entry) =>
                relevantCollectionIds.has(String(entry?.collection))
            );

            const generationData = {
                iterationRecords: filteredIterations,
                collectionSampleMaps: filteredMaps,
                collectionsData: normalizeListPayload(collectionsResponse.data).filter(
                    (collection) => String(collection?.exploration) === explorationId
                ),
                laserDesignParameterTypes: normalizeListPayload(designParamTypesResponse.data),
                laserDesignParameters: [],
                colorMeasurementSets: [],
                widthMeasurementSets: [],
                measurementRgb: [],
                measurementCielab: [],
                measurementCiexyz: [],
                measurementPixel: [],
                measurementMillimeter: [],
                sourcePatches: [],
                sourcePatchColors: [],
            };

            setIterationRecords(generationData.iterationRecords);
            setCollectionSampleMaps(generationData.collectionSampleMaps);
            setCollectionsData(generationData.collectionsData);
            setLaserDesignParameters(generationData.laserDesignParameters);
            setLaserDesignParameterTypes(generationData.laserDesignParameterTypes);
            setColorMeasurementSets(generationData.colorMeasurementSets);
            setWidthMeasurementSets(generationData.widthMeasurementSets);
            setMeasurementRgb(generationData.measurementRgb);
            setMeasurementCielab(generationData.measurementCielab);
            setMeasurementCiexyz(generationData.measurementCiexyz);
            setMeasurementPixel(generationData.measurementPixel);
            setMeasurementMillimeter(generationData.measurementMillimeter);
            setSourcePatches(generationData.sourcePatches);
            setSourcePatchColors(generationData.sourcePatchColors);
            return generationData;
        } catch (error) {
            console.error("Error loading generation data:", error);
            setGenerationLoadError("Could not load generation lineage data.");
            setIterationRecords([]);
            setCollectionSampleMaps([]);
            setCollectionsData([]);
            setLaserDesignParameters([]);
            setLaserDesignParameterTypes([]);
            setColorMeasurementSets([]);
            setWidthMeasurementSets([]);
            setMeasurementRgb([]);
            setMeasurementCielab([]);
            setMeasurementCiexyz([]);
            setMeasurementPixel([]);
            setMeasurementMillimeter([]);
            setSourcePatches([]);
            setSourcePatchColors([]);
            return null;
        }
    };

    const getMemberDesignData = async (explorationId, sampleId) => {
        if (!explorationId || !sampleId) {
            return {
                laserDesignParameters: [],
                colorMeasurementSets: [],
                widthMeasurementSets: [],
                measurementRgb: [],
                measurementCielab: [],
                measurementCiexyz: [],
                measurementPixel: [],
                measurementMillimeter: [],
                sourcePatches: [],
                sourcePatchColors: [],
            };
        }
        const requestConfig = { timeout: 20000 };
        const [
            designParamsResponse,
            colorMeasurementSetResponse,
            widthMeasurementSetResponse,
            sourcePatchResponse,
            sourcePatchColorResponse,
        ] = await Promise.all([
            axios.get(
                "api/color/explorer/laser/design/parameter/?exploration=" +
                explorationId +
                "&sample=" +
                sampleId,
                requestConfig
            ),
            axios.get(
                "api/color/explorer/measurement/color/set/?exploration=" +
                explorationId +
                "&sample=" +
                sampleId,
                requestConfig
            ),
            axios.get(
                "api/color/explorer/measurement/width/set/?exploration=" +
                explorationId +
                "&sample=" +
                sampleId,
                requestConfig
            ),
            axios.get(
                "api/color/explorer/source/patch/?exploration=" +
                explorationId +
                "&sample=" +
                sampleId,
                requestConfig
            ),
            axios.get(
                "api/color/explorer/source/patch-color/?exploration=" +
                explorationId +
                "&sample=" +
                sampleId,
                requestConfig
            ),
        ]);

        const colorMeasurementSets = normalizeListPayload(colorMeasurementSetResponse.data);
        const widthMeasurementSets = normalizeListPayload(widthMeasurementSetResponse.data);
        const rgbIds = Array.from(
            new Set(
                colorMeasurementSets
                    .map((entry) => String(entry?.measurement_rgb || ""))
                    .filter(Boolean)
            )
        );
        const cielabIds = Array.from(
            new Set(
                colorMeasurementSets
                    .map((entry) => String(entry?.measurement_cielab || ""))
                    .filter(Boolean)
            )
        );
        const ciexyzIds = Array.from(
            new Set(
                colorMeasurementSets
                    .map((entry) => String(entry?.measurement_ciexyz || ""))
                    .filter(Boolean)
            )
        );
        const pixelIds = Array.from(
            new Set(
                widthMeasurementSets
                    .map((entry) => String(entry?.measurement_pixel || ""))
                    .filter(Boolean)
            )
        );
        const millimeterIds = Array.from(
            new Set(
                widthMeasurementSets
                    .map((entry) => String(entry?.measurement_millimeter || ""))
                    .filter(Boolean)
            )
        );

        const fetchByIds = async (baseUrl, ids) => {
            if (!ids.length) return [];
            const response = await axios.get(
                baseUrl + "?ids=" + encodeURIComponent(ids.join(",")),
                requestConfig
            );
            return normalizeListPayload(response.data);
        };

        const [
            measurementRgb,
            measurementCielab,
            measurementCiexyz,
            measurementPixel,
            measurementMillimeter,
        ] = await Promise.all([
            fetchByIds("api/color/explorer/measurement/rgb/", rgbIds),
            fetchByIds("api/color/explorer/measurement/cielab/", cielabIds),
            fetchByIds("api/color/explorer/measurement/ciexyz/", ciexyzIds),
            fetchByIds("api/color/explorer/measurement/pixel/", pixelIds),
            fetchByIds("api/color/explorer/measurement/millimeter/", millimeterIds),
        ]);

        return {
            laserDesignParameters: normalizeListPayload(designParamsResponse.data),
            colorMeasurementSets,
            widthMeasurementSets,
            measurementRgb,
            measurementCielab,
            measurementCiexyz,
            measurementPixel,
            measurementMillimeter,
            sourcePatches: normalizeListPayload(sourcePatchResponse.data),
            sourcePatchColors: normalizeListPayload(sourcePatchColorResponse.data),
        };
    };

    // TODO: note that useEffect runs after render()
    useEffect(() => {
        let isMounted = true;
        const loadInitialData = async () => {
            const isAnalyseMode = pageMode === "analyse";
            if (isAnalyseMode) {
                setIsAnalyseInitialDataLoading(true);
            }
            try {
                const displayIter = await getIterationNumber();
                await Promise.all([
                    getExplorationObjective(displayIter),
                    getCumulativeWidthData(displayIter),
                    getCumulativeColorData(displayIter),
                ]);
            } finally {
                if (isMounted && isAnalyseMode) {
                    setIsAnalyseInitialDataLoading(false);
                }
            }
        };
        loadInitialData();
        return () => {
            isMounted = false;
        };
    }, [pageMode]);

    const validTime = parseOptionalThreshold(validTimeText);
    const validLineCount = parseLineCount(validLineCountText);
    const validThresholds = {
        maximumWidthUm: parseThreshold(validMaxWidthText),
        maximumTimePerCm2: validTime.value,
        lineCount: validLineCount.value,
        chromaticityThreshold: parseThreshold(validChromaticityText),
        lightnessThreshold: parseThreshold(validLightnessText),
    };
    const validThresholdsOk =
        validThresholds.maximumWidthUm !== null && validTime.ok && validLineCount.ok &&
        validThresholds.chromaticityThreshold !== null && validThresholds.lightnessThreshold !== null;
    useEffect(() => {
        if (!explorationId || !validThresholdsOk) return;
        getValidSamplesData(validThresholds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [explorationId, validThresholds.maximumWidthUm, validThresholds.maximumTimePerCm2, validThresholds.lineCount,
        validThresholds.chromaticityThreshold, validThresholds.lightnessThreshold]);

    useEffect(() => {
        const shouldLoadGenerationData = pageMode === "analyse" && resultsTab === 4;
        if (!shouldLoadGenerationData || isGenerationDataLoading || hasLoadedGenerationData) {
            return;
        }
        setIsGenerationDataLoading(true);
        (async () => {
            try {
                const result = await getGenerationData();
                if (result) {
                    setHasLoadedGenerationData(true);
                }
            } finally {
                setIsGenerationDataLoading(false);
            }
        })();
    }, [pageMode, resultsTab, isGenerationDataLoading, hasLoadedGenerationData]);

    useEffect(() => {
        const shouldLoadMemberData =
            pageMode === "analyse" &&
            resultsTab === 4 &&
            hasLoadedGenerationData &&
            Boolean(selectedMemberId);
        if (!shouldLoadMemberData) {
            if (!selectedMemberId) {
                setLaserDesignParameters([]);
                setColorMeasurementSets([]);
                setWidthMeasurementSets([]);
                setMeasurementRgb([]);
                setMeasurementCielab([]);
                setMeasurementCiexyz([]);
                setMeasurementPixel([]);
                setMeasurementMillimeter([]);
                setSourcePatches([]);
                setSourcePatchColors([]);
            }
            return;
        }
        let cancelled = false;
        const sampleId = String(selectedMemberId || "");
        setIsMemberDataLoading(true);
        getMemberDesignData(explorationId, sampleId)
            .then((data) => {
                if (cancelled) return;
                setLaserDesignParameters(data.laserDesignParameters);
                setColorMeasurementSets(data.colorMeasurementSets);
                setWidthMeasurementSets(data.widthMeasurementSets);
                setMeasurementRgb(data.measurementRgb);
                setMeasurementCielab(data.measurementCielab);
                setMeasurementCiexyz(data.measurementCiexyz);
                setMeasurementPixel(data.measurementPixel);
                setMeasurementMillimeter(data.measurementMillimeter);
                setSourcePatches(data.sourcePatches);
                setSourcePatchColors(data.sourcePatchColors);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error("Error loading member design data:", error);
            })
            .finally(() => {
                if (!cancelled) {
                    setIsMemberDataLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [pageMode, resultsTab, hasLoadedGenerationData, selectedMemberId]);

    // The exploration's latest iteration is the default; a deep link (see
    // deepLinkRef below) wins the first time the number becomes known.
    const deepLinkRef = useRef({
        sampleId: location?.state?.sampleId ? String(location.state.sampleId) : "",
        iteration: location?.state?.iteration != null ? Number(location.state.iteration) : null,
        collectionRole: location?.state?.collectionRole ? String(location.state.collectionRole) : "",
    });
    useEffect(() => {
        // The deep link keeps winning until its sample has been selected (the
        // iteration number arrives after the mount, in a second run).
        const pending = deepLinkRef.current;
        if (pending.sampleId && pending.iteration != null) {
            setAnalysisIteration(pending.iteration);
            return;
        }
        setAnalysisIteration(iterationNumber);
    }, [iterationNumber]);

    useEffect(() => {
        const widthValues = (widthData || [])
            .map((item) => Number(item?.cluster_width))
            .filter((value) => Number.isFinite(value) && value > -1);
        if (widthValues.length === 0) {
            setWidthGrowthMin(0);
            setWidthGrowthMax(0);
            return;
        }
        const minValue = Math.min(...widthValues);
        const maxValue = Math.max(...widthValues);
        let nextMin = floorTo3(minValue);
        let nextMax = ceilTo3(maxValue);
        if (nextMax <= nextMin) {
            nextMax = roundTo3(nextMin + 0.001);
        }
        setWidthGrowthMin(nextMin);
        setWidthGrowthMax(nextMax);
    }, [widthData]);

    const handleNextGenerationSource = (source) => {
        setNextGenerationSource(source);
        setNextGenerationError(null);
        if (source !== "upload") {
            injectedCheckSeq.current += 1;
            setInjectedFile(null);
            setInjectedCheck(null);
        }
    };

    const handleInjectedFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!event.target) return;
        event.target.value = "";
        if (!file) return;
        const seq = injectedCheckSeq.current + 1;
        injectedCheckSeq.current = seq;
        setNextGenerationError(null);
        let parsed;
        try {
            parsed = JSON.parse(await file.text());
        } catch {
            setInjectedFile(null);
            setInjectedCheck({ status: "refused", error: { error: "Invalid JSON file — could not parse." } });
            return;
        }
        setInjectedFile({ name: file.name, points: parsed });
        setInjectedCheck({ status: "checking" });
        try {
            const response = await axios.post("api/exploration/injected-population/check", {
                explorationId,
                points: parsed,
                fileName: file.name,
            });
            if (injectedCheckSeq.current !== seq) return;
            setInjectedCheck({
                status: "ok",
                pointCount: Number(response.data?.point_count) || 0,
                ignoredKeys: Array.isArray(response.data?.ignored_keys) ? response.data.ignored_keys : [],
            });
        } catch (error) {
            if (injectedCheckSeq.current !== seq) return;
            const data = error?.response?.data;
            setInjectedCheck({
                status: "refused",
                error: data && (data.error || data.code)
                    ? data
                    : { error: error?.message || "The population file could not be checked." },
            });
        }
    };

    // Next is available with the genetic algorithm, or once the chosen file
    // passed the server's check.
    const nextGenerationReady =
        nextGenerationSource !== "upload" || (injectedFile !== null && injectedCheck?.status === "ok");

    // Next prepares the next generation (api/next/iteration/data ranks the
    // measured samples, keeps the best ones and breeds the next population -
    // or takes the uploaded one) and then opens its cluster marking. A
    // refused upload or a failed request keeps this page, with the reason
    // shown in the Next generation panel.
    const nextPageClickFunc = async (navigatePageName) => {
        if (nextGenerationBusy || !nextGenerationReady) return;
        const injecting = nextGenerationSource === "upload";
        setNextGenerationError(null);
        setNextGenerationBusy(true);
        try {
            const payload = { explorationId };
            if (injecting) {
                payload.points = injectedFile.points;
                payload.fileName = injectedFile.name;
            }
            await axios.post("api/next/iteration/data", payload);
            navigate(navigatePageName);
        } catch (error) {
            console.error("Preparing the next generation failed:", error);
            const data = error?.response?.data;
            setNextGenerationError(
                data && (data.error || data.code)
                    ? data
                    : { error: error?.message || "Preparing the next generation failed." }
            );
            setNextGenerationBusy(false);
            // The error is reported in the Next generation tab: open it.
            setResultsTab(Math.max(0, tabDefinitions.findIndex((tab) => tab.key === "nextGeneration")));
        }
    };

    const isAnalysePage = pageMode === "analyse";
    // A completed exploration has no next iteration to mark: a manual start
    // without the optimizer completes after P0 (its row says so even before
    // the status is written), any other once its last iteration is done.
    const explorationCompleted =
        String(explorationData?.status || "").toUpperCase() === "COMPLETED" ||
        explorationData?.manual_start?.continue_with_optimizer === false;
    const pageTitle = isAnalysePage ? "Analyse Exploration" : "Results";
    const pageSubtitle = isAnalysePage
        ? "Review this exploration's summary, design space, and performance across iterations."
        : "Review width and color performance across iterations.";
    const prevPage = EXPLORATION_LIST_ROUTE;

    const tabDefinitions = isAnalysePage
        ? [
            { key: "overview", label: "Summary" },
            { key: "width", label: "Width Distribution" },
            { key: "widthGrowth", label: "Evolution" },
            { key: "color", label: "Color Distribution" },
            { key: "designPerformance", label: "Design & performance" },
            { key: "validSamples", label: "Valid samples" },
            ...(blackWhiteExploration ? [{ key: "whiteBlack", label: "L* vs chromaticity" }] : []),
            { key: "plates", label: "Plates" },
            { key: "configs", label: "Configurations" },
        ]
        : [
            { key: "width", label: "Width Distribution" },
            { key: "widthGrowth", label: "Evolution" },
            { key: "color", label: "Color Distribution" },
            { key: "validSamples", label: "Valid samples" },
            ...(blackWhiteExploration ? [{ key: "whiteBlack", label: "L* vs chromaticity" }] : []),
            ...(explorationCompleted ? [] : [{ key: "nextGeneration", label: "Next generation" }]),
        ];

    const safeTabIndex =
        resultsTab >= 0 && resultsTab < tabDefinitions.length ? resultsTab : 0;
    const activeTabKey = tabDefinitions[safeTabIndex]?.key;

    // Keep the selected index inside the live tab list: the trailing
    // "L* vs chromaticity" tab disappears once the exploration turns out
    // not to be a black/white one, and a stale index past the end would
    // otherwise snap back to the first tab. (An older guard hard-coded
    // index 2 for this, which became "Color Distribution" when the
    // Evolution tab was inserted before it and kicked the user off it.)
    useEffect(() => {
        if (resultsTab >= tabDefinitions.length) {
            setResultsTab(Math.max(0, tabDefinitions.length - 1));
        }
    }, [resultsTab, tabDefinitions.length]);

    // Allow other pages to deep-link directly to a specific Analyse tab via
    // ``navigate(explorationRoute(id, "analyse"), { state: { initialTab: "plates" } })``.
    // Resolve the requested key against the live tab list (which can change
    // depending on ``blackWhiteExploration``) and clear the state once applied
    // so revisits don't keep snapping back to the deep-linked tab.
    useEffect(() => {
        if (!isAnalysePage) return;
        const requestedKey = location?.state?.initialTab;
        if (!requestedKey) return;
        const idx = tabDefinitions.findIndex((t) => t.key === requestedKey);
        if (idx >= 0 && idx !== resultsTab) {
            setResultsTab(idx);
        }
        if (typeof window !== "undefined" && window.history?.replaceState) {
            window.history.replaceState({}, document.title);
        }
        // Intentionally only react to the navigation-state and tab list. We do
        // not depend on ``resultsTab`` to avoid bouncing back after the user
        // manually clicks another tab.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnalysePage, location?.state?.initialTab, tabDefinitions.length]);

    const getUniqueIterations = (items) => {
        const unique = new Set(
            (items || [])
                .map((item) => Number(item?.iteration_number))
                .filter((value) => Number.isInteger(value))
        );
        return Array.from(unique).sort((a, b) => a - b);
    };

    const widthIterations = getUniqueIterations(widthData);
    const colorIterations = getUniqueIterations(colorData);
    const whiteBlackIterations = getUniqueIterations(blackWhitecolorData);
    const allIterations = Array.from(
        new Set([...widthIterations, ...colorIterations, ...whiteBlackIterations])
    ).sort((a, b) => a - b);
    const samplePointsCount = Math.max(widthData.length, colorData.length);

    const selectedIteration =
        allIterations.includes(analysisIteration)
            ? analysisIteration
            : (allIterations.length ? allIterations[allIterations.length - 1] : 0);

    const widthForSelectedIteration = widthData.filter(
        (item) => Number(item.iteration_number) === selectedIteration
    );
    const colorForSelectedIteration = colorData.filter(
        (item) => Number(item.iteration_number) === selectedIteration
    );

    const allValidWidthValues = (widthData || [])
        .map((item) => Number(item?.cluster_width))
        .filter((value) => Number.isFinite(value) && value > -1);
    const widthGlobalMin = allValidWidthValues.length ? Math.min(...allValidWidthValues) : 0;
    const widthGlobalMax = allValidWidthValues.length ? Math.max(...allValidWidthValues) : 0;
    const widthAllowedMin = floorTo3(widthGlobalMin);
    const widthAllowedMax = ceilTo3(widthGlobalMax);
    const widthGrowthRangeValid =
        Number.isFinite(widthGrowthMin) &&
        Number.isFinite(widthGrowthMax) &&
        widthGrowthMin >= widthAllowedMin &&
        widthGrowthMax <= widthAllowedMax &&
        widthGrowthMin < widthGrowthMax;

    const widthGrowthFiltered = (widthData || []).filter((item) => {
        const width = Number(item?.cluster_width);
        const iteration = Number(item?.iteration_number);
        if (!Number.isFinite(width) || width <= -1) return false;
        if (!Number.isFinite(iteration)) return false;
        return width >= widthGrowthMin && width <= widthGrowthMax;
    });

    const widthGrowthByIteration = widthGrowthFiltered.reduce((accumulator, entry) => {
        const key = Number(entry.iteration_number);
        const value = Number(entry.cluster_width);
        if (!accumulator[key]) {
            accumulator[key] = { sum: 0, count: 0 };
        }
        accumulator[key].sum += value;
        accumulator[key].count += 1;
        return accumulator;
    }, {});

    const widthGrowthData = Object.keys(widthGrowthByIteration)
        .map((key) => {
            const iter = Number(key);
            const record = widthGrowthByIteration[key];
            return {
                iteration: iter,
                average_width: record.count > 0 ? record.sum / record.count : null,
                sample_count: record.count,
            };
        })
        .filter((row) => row.average_width != null)
        .sort((a, b) => a.iteration - b.iteration);

    const sampleIdsByCollection = new Map();
    collectionSampleMaps.forEach((entry) => {
        const collectionId = String(entry?.collection || "");
        if (!collectionId) return;
        const sampleId = String(entry?.sample || "");
        if (!sampleIdsByCollection.has(collectionId)) {
            sampleIdsByCollection.set(collectionId, new Set());
        }
        if (sampleId) {
            sampleIdsByCollection.get(collectionId).add(sampleId);
        }
    });

    const getCollectionSampleSet = (collectionId) => {
        if (!collectionId) return new Set();
        return sampleIdsByCollection.get(String(collectionId)) || new Set();
    };

    const countIntersection = (leftSet, rightSet) => {
        let count = 0;
        leftSet.forEach((value) => {
            if (rightSet.has(value)) count += 1;
        });
        return count;
    };
    // Build chromaticity evolution directly from the already loaded result streams
    // so it remains available even when design/performance detail data is lazy-loaded.
    const widthValuesByIteration = new Map();
    (widthData || []).forEach((entry) => {
        const iter = Number(entry?.iteration_number);
        const width = Number(entry?.cluster_width);
        if (!Number.isFinite(iter) || !Number.isFinite(width) || width <= -1) return;
        if (!widthValuesByIteration.has(iter)) {
            widthValuesByIteration.set(iter, []);
        }
        widthValuesByIteration.get(iter).push(width);
    });
    const chromaticityGrowthData = allIterations
        .map((iter) => {
            const iterationNumberValue = Number(iter);
            if (!Number.isFinite(iterationNumberValue)) return null;
            const widthValuesForIteration = widthValuesByIteration.get(iterationNumberValue) || [];
            const hasAnyWidthInRange = widthValuesForIteration.some(
                (value) => value >= widthGrowthMin && value <= widthGrowthMax
            );
            if (!hasAnyWidthInRange) return null;

            const values = (colorData || [])
                .filter((item) => Number(item?.iteration_number) === iterationNumberValue)
                .filter((item) => {
                    const pointWidth = Number(item?.cluster_width);
                    // If per-point width exists, apply filter to that point.
                    if (Number.isFinite(pointWidth)) {
                        return pointWidth >= widthGrowthMin && pointWidth <= widthGrowthMax;
                    }
                    // Otherwise include points when the iteration has any width in range.
                    return true;
                })
                .map((item) => computeChromaticity(item?.a_value, item?.b_value))
                .filter((value) => Number.isFinite(value));
            if (values.length === 0) return null;
            const sum = values.reduce((accumulator, value) => accumulator + value, 0);
            return {
                iteration: iterationNumberValue,
                average_chromaticity: sum / values.length,
                sample_count: values.length,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.iteration - b.iteration);

    const generationRows = iterationRecords.map((iteration) => {
        const inputSamples = getCollectionSampleSet(iteration?.input_collection);
        const generatedSamples = getCollectionSampleSet(iteration?.generated_collection);
        const outputSamples = getCollectionSampleSet(iteration?.output_collection);
        // Parents that made it into the 49 best vs. parents that were ranked out.
        const promotedCount = countIntersection(inputSamples, outputSamples);
        const killedCount = Math.max(inputSamples.size - promotedCount, 0);

        return {
            iteration_number: Number(iteration?.iteration_number ?? 0),
            input_collection: iteration?.input_collection || null,
            generated_collection: iteration?.generated_collection || null,
            output_collection: iteration?.output_collection || null,
            parents_count: inputSamples.size,
            children_count: generatedSamples.size,
            promoted_count: promotedCount,
            killed_count: killedCount,
            raw: iteration,
        };
    });
    const parameterTypeNameById = new Map(
        laserDesignParameterTypes.map((item) => [String(item?.id || ""), item?.parameter_name || item?.name || "parameter"])
    );
    const rgbById = new Map(
        measurementRgb.map((item) => [String(item?.id || ""), item])
    );
    const cielabById = new Map(
        measurementCielab.map((item) => [String(item?.id || ""), item])
    );
    const ciexyzById = new Map(
        measurementCiexyz.map((item) => [String(item?.id || ""), item])
    );
    const pixelById = new Map(
        measurementPixel.map((item) => [String(item?.id || ""), item])
    );
    const millimeterById = new Map(
        measurementMillimeter.map((item) => [String(item?.id || ""), item])
    );
    const selectedGeneration = generationRows.find(
        (row) => Number(row.iteration_number) === Number(selectedIteration)
    ) || null;
    const generationCollectionIdForRole = selectedGeneration
        ? (
            generationCollectionRole === "input"
                ? selectedGeneration.input_collection
                : generationCollectionRole === "combined"
                    ? selectedGeneration.generated_collection
                    : selectedGeneration.output_collection
        )
        : null;
    const membersForSelectedGenerationCollection = collectionSampleMaps
        .filter((entry) => String(entry?.collection || "") === String(generationCollectionIdForRole || ""))
        .sort((a, b) => {
            const rankA = a?.rank ?? Number.MAX_SAFE_INTEGER;
            const rankB = b?.rank ?? Number.MAX_SAFE_INTEGER;
            if (rankA !== rankB) return rankA - rankB;
            return String(a?.sample || "").localeCompare(String(b?.sample || ""));
        });

    useEffect(() => {
        if (membersForSelectedGenerationCollection.length > 0) {
            setGenerationSelectedMemberId(String(membersForSelectedGenerationCollection[0].sample));
        } else {
            setGenerationSelectedMemberId("");
        }
    }, [
        selectedGeneration?.iteration_number,
        generationCollectionRole,
        generationCollectionIdForRole,
        membersForSelectedGenerationCollection.length,
    ]);

    const selectedGenerationMemberEntry = membersForSelectedGenerationCollection.find(
        (entry) => String(entry?.sample || "") === String(generationSelectedMemberId || "")
    ) || null;
    const selectedGenerationCollectionMeta = collectionsData.find(
        (collection) => String(collection?.id || "") === String(generationCollectionIdForRole || "")
    ) || null;
    const generationDesignParametersForMember = laserDesignParameters
        .filter((item) => String(item?.sample || "") === String(generationSelectedMemberId || ""))
        .map((item) => ({
            id: item?.id,
            key: parameterTypeNameById.get(String(item?.laser_design_parameter_type_enum || "")) || "parameter",
            value: item?.value,
        }))
        .sort((a, b) => String(a.key).localeCompare(String(b.key)));
    const generationInputSampleSet = new Set(
        collectionSampleMaps
            .filter((entry) => String(entry?.collection || "") === String(selectedGeneration?.input_collection || ""))
            .map((entry) => String(entry?.sample || ""))
    );
    const generationCombinedSampleSet = new Set(
        collectionSampleMaps
            .filter((entry) => String(entry?.collection || "") === String(selectedGeneration?.generated_collection || ""))
            .map((entry) => String(entry?.sample || ""))
    );
    const generationOutputSampleSet = new Set(
        collectionSampleMaps
            .filter((entry) => String(entry?.collection || "") === String(selectedGeneration?.output_collection || ""))
            .map((entry) => String(entry?.sample || ""))
    );
    const generationMemberInInput = generationInputSampleSet.has(String(generationSelectedMemberId || ""));
    const generationMemberInCombined = generationCombinedSampleSet.has(String(generationSelectedMemberId || ""));
    const generationMemberInOutput = generationOutputSampleSet.has(String(generationSelectedMemberId || ""));
    const generationMemberLifecycle = generationMemberInInput && generationMemberInCombined && generationMemberInOutput
        ? "Promoted and retained"
        : generationMemberInInput && generationMemberInCombined && !generationMemberInOutput
            ? "Promoted then not selected"
            : generationMemberInInput && !generationMemberInCombined
                ? "Killed after ranking"
                : !generationMemberInInput && generationMemberInOutput
                    ? "New child in output"
                    : generationMemberInCombined
                        ? "Generated-only candidate"
                        : "Unknown";

    const iterationRecordForBrowser = iterationRecords.find(
        (row) => Number(row.iteration_number) === Number(selectedIteration)
    ) || iterationRecords[iterationRecords.length - 1] || null;

    const collectionIdForRole = iterationRecordForBrowser
        ? (
            selectedCollectionRole === "input"
                ? iterationRecordForBrowser.input_collection
                : selectedCollectionRole === "combined"
                    ? iterationRecordForBrowser.generated_collection
                    : iterationRecordForBrowser.output_collection
        )
        : null;

    const membersForSelectedCollection = collectionSampleMaps
        .filter((entry) => String(entry?.collection || "") === String(collectionIdForRole || ""))
        .sort((a, b) => {
            const rankA = a?.rank ?? Number.MAX_SAFE_INTEGER;
            const rankB = b?.rank ?? Number.MAX_SAFE_INTEGER;
            if (rankA !== rankB) return rankA - rankB;
            return String(a?.sample || "").localeCompare(String(b?.sample || ""));
        });

    // Deep link from another page (e.g. the selection overview's sample origin
    // card): ``state.sampleId`` (+ ``iteration`` / ``collectionRole``) picks
    // that member once the collection maps are loaded, instead of the first one.
    useEffect(() => {
        const pending = deepLinkRef.current;
        if (pending.collectionRole) {
            setSelectedCollectionRole(pending.collectionRole);
            pending.collectionRole = "";
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const pending = deepLinkRef.current;
        const wanted = pending.sampleId;
        if (wanted && membersForSelectedCollection.some((entry) => String(entry.sample) === wanted)) {
            pending.sampleId = "";
            pending.iteration = null;
            setSelectedMemberId(wanted);
            return;
        }
        if (wanted) {
            // Keep waiting while the deep-linked collection is not the one
            // shown yet (data still loading); give up once it is and the
            // sample is not among its members.
            const targetShown = pending.iteration == null || Number(selectedIteration) === Number(pending.iteration);
            const roleShown = !pending.collectionRole || selectedCollectionRole === pending.collectionRole;
            if (!(targetShown && roleShown && membersForSelectedCollection.length > 0)) return;
            pending.sampleId = "";
            pending.iteration = null;
        }
        if (membersForSelectedCollection.length > 0) {
            setSelectedMemberId(String(membersForSelectedCollection[0].sample));
        } else {
            setSelectedMemberId("");
        }
    }, [selectedIteration, selectedCollectionRole, collectionIdForRole, membersForSelectedCollection.length]);

    const selectedMemberEntry = membersForSelectedCollection.find(
        (entry) => String(entry?.sample || "") === String(selectedMemberId || "")
    ) || null;

    const selectedCollectionMeta = collectionsData.find(
        (collection) => String(collection?.id || "") === String(collectionIdForRole || "")
    ) || null;

    useEffect(() => {
        let cancelled = false;
        setCollectionMarking(null);
        if (!explorationId || !collectionIdForRole || activeTabKey !== "designPerformance") {
            return undefined;
        }
        setIsLoadingCollectionMarking(true);
        axios
            .get(`api/exploration/${explorationId}/collection/${collectionIdForRole}/marking/`)
            .then((response) => {
                if (cancelled) return;
                setCollectionMarking(response.data);
            })
            .catch(() => {
                if (cancelled) return;
                // Not knowing whether it was marked must not blank the card.
                setCollectionMarking(null);
            })
            .finally(() => {
                if (cancelled) return;
                setIsLoadingCollectionMarking(false);
            });
        return () => {
            cancelled = true;
        };
    }, [explorationId, collectionIdForRole, activeTabKey]);

    // Load the patch grid of every marking that holds members of the browsed
    // collection. The color pass is preferred so patches carry their measured
    // tint; the cluster pass is the fallback for width-only explorations.
    useEffect(() => {
        let cancelled = false;
        setMarkingPatchGrids({});
        const markings = collectionMarking?.markings || [];
        const requests = markings
            .map((marking) => {
                const passes = marking?.passes || [];
                const pass = passes.find((entry) => entry.pass_kind === "COLOR" && entry.plate_id)
                    || passes.find((entry) => entry.plate_id);
                if (!pass) return null;
                return { patchGroupId: marking.patch_group_id, plateId: pass.plate_id, passKind: pass.pass_kind };
            })
            .filter(Boolean);
        if (!explorationId || requests.length === 0) {
            setIsLoadingMarkingPatchGrids(false);
            return undefined;
        }
        setIsLoadingMarkingPatchGrids(true);
        Promise.all(
            requests.map((request) =>
                axios
                    .get(
                        `api/exploration/${explorationId}/plates/${request.plateId}/patch-group/${request.patchGroupId}/patches/`,
                        { params: { pass_kind: request.passKind } }
                    )
                    .then((response) => [request.patchGroupId, { ...response.data, passKind: request.passKind }])
                    .catch(() => null)
            )
        )
            .then((entries) => {
                if (cancelled) return;
                const next = {};
                entries.filter(Boolean).forEach(([patchGroupId, data]) => {
                    next[patchGroupId] = data;
                });
                setMarkingPatchGrids(next);
            })
            .finally(() => {
                if (cancelled) return;
                setIsLoadingMarkingPatchGrids(false);
            });
        return () => {
            cancelled = true;
        };
    }, [explorationId, collectionMarking]);

    const memberSampleIdSet = new Set(
        membersForSelectedCollection.map((entry) => String(entry?.sample || ""))
    );
    const markingsWithGrid = (collectionMarking?.markings || []).filter(
        (marking) => Boolean(markingPatchGrids[marking.patch_group_id])
    );
    // A generated population is marked as a unit and is shown as it lies on
    // the plate. A pooled collection (input / output) traces back to many
    // markings and is shown as one provenance chart instead.
    const ownMarkingsWithGrid = markingsWithGrid.filter((marking) => marking.origin?.is_own_collection);
    const showPhysicalPatchGroups = ownMarkingsWithGrid.length > 0;
    const memberOriginEntries = (() => {
        if (showPhysicalPatchGroups) return [];
        const originBySample = new Map();
        for (const marking of markingsWithGrid) {
            const grid = markingPatchGrids[marking.patch_group_id];
            for (const patch of grid?.patches || []) {
                const sampleId = String(patch.sample_id);
                if (!originBySample.has(sampleId)) {
                    originBySample.set(sampleId, { marking, patch, passKind: grid.passKind });
                }
            }
        }
        return membersForSelectedCollection.map((entry) => {
            const sampleId = String(entry?.sample || "");
            const origin = originBySample.get(sampleId);
            return {
                sampleId,
                rank: entry?.rank ?? null,
                originLabel: origin ? originLabelOf(origin.marking) : "Unmarked",
                originIteration: origin ? (origin.marking.origin?.iteration_number ?? -1) : null,
                batchNumber: origin ? origin.marking.batch_number : null,
                patch: origin ? origin.patch : null,
                passKind: origin ? origin.passKind : null,
            };
        });
    })();
    const memberOriginIterationCount = new Set(
        memberOriginEntries.filter((entry) => entry.patch).map((entry) => entry.originLabel)
    ).size;

    const designParametersForMember = laserDesignParameters
        .filter((item) => String(item?.sample || "") === String(selectedMemberId || ""))
        .map((item) => ({
            id: item?.id,
            key: parameterTypeNameById.get(String(item?.laser_design_parameter_type_enum || "")) || "parameter",
            value: item?.value,
        }))
        .sort((a, b) => String(a.key).localeCompare(String(b.key)));


    // The iteration this sample comes from: created for P0 (iteration 0's
    // input collection) or for some iteration's generated collection (Q_i).
    const memberOrigin = (() => {
        const sampleId = String(selectedMemberId || "");
        if (!sampleId) return null;
        for (const record of iterationRecords) {
            if (getCollectionSampleSet(record?.generated_collection).has(sampleId)) {
                return { label: `Q${record.iteration_number}`, iteration: Number(record.iteration_number), collectionId: String(record.generated_collection) };
            }
        }
        const firstRecord = iterationRecords.find((record) => Number(record?.iteration_number) === 0);
        if (firstRecord && getCollectionSampleSet(firstRecord.input_collection).has(sampleId)) {
            return { label: "P0", iteration: 0, collectionId: String(firstRecord.input_collection) };
        }
        return null;
    })();

    // Where on the plate this sample was marked (row/column inside its patch
    // group). Only meaningful when the collection browsed on the left is the
    // one that was physically marked.
    const memberMarkingPosition = (() => {
        if (!memberOrigin || String(collectionIdForRole || "") !== memberOrigin.collectionId) {
            return null;
        }
        const bySample = (entry) => String(entry?.sample || "") === String(selectedMemberId || "");
        const patch = sourcePatchColors.find(bySample) || sourcePatches.find(bySample);
        if (!patch || patch.x_coordinate === undefined || patch.x_coordinate === null) {
            return null;
        }
        return { row: patch.x_coordinate, column: patch.y_coordinate };
    })();
    const colorMeasurementsForMember = colorMeasurementSets
        .filter((entry) => String(entry?.sample || "") === String(selectedMemberId || ""))
        .map((entry) => {
            const rgb = rgbById.get(String(entry?.measurement_rgb || ""));
            const cielab = cielabById.get(String(entry?.measurement_cielab || ""));
            const ciexyz = ciexyzById.get(String(entry?.measurement_ciexyz || ""));
            return {
                id: entry?.id,
                measurement_rgb_id: entry?.measurement_rgb || null,
                measurement_cielab_id: entry?.measurement_cielab || null,
                measurement_ciexyz_id: entry?.measurement_ciexyz || null,
                rgb: rgb
                    ? {
                        r: rgb?.r_value,
                        g: rgb?.g_value,
                        b: rgb?.b_value,
                    }
                    : null,
                cielab: cielab
                    ? {
                        l: cielab?.l_star_value,
                        a: cielab?.a_star_value,
                        b: cielab?.b_star_value,
                    }
                    : null,
                ciexyz: ciexyz
                    ? {
                        x: ciexyz?.x_value ?? ciexyz?.x_star_value,
                        y: ciexyz?.y_value ?? ciexyz?.y_star_value,
                        z: ciexyz?.z_value ?? ciexyz?.z_star_value,
                    }
                    : null,
            };
        });
    const widthMeasurementsForMember = widthMeasurementSets
        .filter((entry) => String(entry?.sample || "") === String(selectedMemberId || ""))
        .map((entry) => {
            const pixel = pixelById.get(String(entry?.measurement_pixel || ""));
            const millimeter = millimeterById.get(String(entry?.measurement_millimeter || ""));
            return {
                id: entry?.id,
                measurement_pixel_id: entry?.measurement_pixel || null,
                measurement_millimeter_id: entry?.measurement_millimeter || null,
                measurement_pixel_value: pixel?.value,
                measurement_millimeter_value: millimeter?.value,
                source_patch_id: entry?.source_patch_cluster || null,
                source_path: entry?.source_path || null,
            };
        });
    const sampleSourcePatchesForMember = sourcePatches
        .filter((entry) => String(entry?.sample || "") === String(selectedMemberId || ""))
        .map((entry) => ({
            id: String(entry?.id || ""),
            type: "Width patch",
            patch_data: entry?.patch_data || null,
        }));
    const sampleSourcePatchColorsForMember = sourcePatchColors
        .filter((entry) => String(entry?.sample || "") === String(selectedMemberId || ""))
        .map((entry) => ({
            id: String(entry?.id || ""),
            type: "Color Patch",
            patch_data: entry?.patch_data || null,
        }));
    // Only offer buttons that can actually open an image: imported
    // historical rows may carry no per-patch crop (patch_data null), and a
    // button that silently does nothing reads as a bug.
    const samplePatchEntriesForMember = [
        ...sampleSourcePatchesForMember,
        ...sampleSourcePatchColorsForMember,
    ].filter((entry) => Boolean(entry?.id) && Boolean(entry?.patch_data));
    const isWidthEvolution = evolutionMetric === "width";
    const evolutionAllowedMin = widthAllowedMin;
    const evolutionAllowedMax = widthAllowedMax;
    const validKind = validSamplesData?.kind || "chromatic";
    // The gamut grows with the iterations: the slider shows it up to one of them.
    const validSamplePoints = Array.isArray(validSamplesData?.samples) ? validSamplesData.samples : [];
    const validSampleIterations = Array.from(
        new Set(validSamplePoints.map((sample) => Number(sample.iteration_number)).filter(Number.isFinite))
    ).sort((left, right) => left - right);
    const validGamutMax = validSampleIterations.length ? validSampleIterations[validSampleIterations.length - 1] : 0;
    const validGamutValue = validGamutIteration === null ? validGamutMax : Math.min(validGamutIteration, validGamutMax);
    const validGamutSamples = validSamplePoints.filter((sample) => Number(sample.iteration_number) <= validGamutValue);
    const validGamutValidCount = validGamutSamples.filter((sample) => sample.valid).length;
    const evolutionMin = widthGrowthMin;
    const evolutionMax = widthGrowthMax;
    const evolutionRangeValid = widthGrowthRangeValid;
    const evolutionChartData = isWidthEvolution
        ? widthGrowthData.map((entry) => ({ ...entry, average_value: entry.average_width }))
        : chromaticityGrowthData.map((entry) => ({ ...entry, average_value: entry.average_chromaticity }));
    const evolutionYAxisLabel = isWidthEvolution ? "Average width" : "Average chromaticity";
    const evolutionNoDataText = isWidthEvolution
        ? "No width points available for the selected filter range."
        : "No chromaticity points available for the selected filter range.";

    const openSamplePatchImage = async (patchEntry) => {
        if (!patchEntry?.id || !patchEntry?.patch_data) return;
        let imageUrl = samplePatchImageUrls[patchEntry.id];
        if (!imageUrl) {
            try {
                const response = await axios.post(
                    "api/patch/file/get",
                    { fileDestination: patchEntry.patch_data },
                    { responseType: "blob" }
                );
                imageUrl = window.URL.createObjectURL(response.data);
                setSamplePatchImageUrls((prev) => ({ ...prev, [patchEntry.id]: imageUrl }));
            } catch (_error) {
                imageUrl = "";
            }
        }
        if (imageUrl) {
            window.open(imageUrl, "_blank", "noopener,noreferrer");
        }
    };
    useEffect(() => {
        setSamplePatchImageUrls({});
    }, [selectedMemberId]);
    const buildSamplePointsForSampleIds = (
        sampleIds,
        {
            laserDesignParametersData = laserDesignParameters,
            laserDesignParameterTypesData = laserDesignParameterTypes,
            colorMeasurementSetsData = colorMeasurementSets,
            widthMeasurementSetsData = widthMeasurementSets,
            measurementRgbData = measurementRgb,
            measurementCielabData = measurementCielab,
            measurementCiexyzData = measurementCiexyz,
            measurementPixelData = measurementPixel,
            measurementMillimeterData = measurementMillimeter,
        } = {}
    ) => {
        const sampleIdSet = new Set((sampleIds || []).map((id) => String(id || "")).filter(Boolean));
        const parameterTypeNameByIdData = new Map(
            (laserDesignParameterTypesData || []).map((item) => [String(item?.id || ""), item?.parameter_name || item?.name || "parameter"])
        );
        const rgbByIdData = new Map(
            (measurementRgbData || []).map((item) => [String(item?.id || ""), item])
        );
        const cielabByIdData = new Map(
            (measurementCielabData || []).map((item) => [String(item?.id || ""), item])
        );
        const ciexyzByIdData = new Map(
            (measurementCiexyzData || []).map((item) => [String(item?.id || ""), item])
        );
        const pixelByIdData = new Map(
            (measurementPixelData || []).map((item) => [String(item?.id || ""), item])
        );
        const millimeterByIdData = new Map(
            (measurementMillimeterData || []).map((item) => [String(item?.id || ""), item])
        );
        const designSpaceBySample = new Map();
        (laserDesignParametersData || [])
            .filter((row) => sampleIdSet.has(String(row?.sample || "")))
            .forEach((row) => {
                const sampleId = String(row?.sample || "");
                if (!designSpaceBySample.has(sampleId)) {
                    designSpaceBySample.set(sampleId, {});
                }
                const parameterName =
                    parameterTypeNameByIdData.get(String(row?.laser_design_parameter_type_enum || "")) || "parameter";
                designSpaceBySample.get(sampleId)[parameterName] = row?.value ?? null;
            });

        const performanceSpaceBySample = new Map();
        (colorMeasurementSetsData || [])
            .filter((row) => sampleIdSet.has(String(row?.sample || "")))
            .forEach((row) => {
                const sampleId = String(row?.sample || "");
                const rgb = rgbByIdData.get(String(row?.measurement_rgb || ""));
                const cielab = cielabByIdData.get(String(row?.measurement_cielab || ""));
                const ciexyz = ciexyzByIdData.get(String(row?.measurement_ciexyz || ""));
                performanceSpaceBySample.set(sampleId, {
                    ...(performanceSpaceBySample.get(sampleId) || {}),
                    r: rgb?.r_value ?? null,
                    g: rgb?.g_value ?? null,
                    b: rgb?.b_value ?? null,
                    l_star: cielab?.l_star_value ?? null,
                    a_star: cielab?.a_star_value ?? null,
                    b_star: cielab?.b_star_value ?? null,
                    x: ciexyz?.x_value ?? ciexyz?.x_star_value ?? null,
                    y: ciexyz?.y_value ?? ciexyz?.y_star_value ?? null,
                    z: ciexyz?.z_value ?? ciexyz?.z_star_value ?? null,
                });
            });
        (widthMeasurementSetsData || [])
            .filter((row) => sampleIdSet.has(String(row?.sample || "")))
            .forEach((row) => {
                const sampleId = String(row?.sample || "");
                const pixel = pixelByIdData.get(String(row?.measurement_pixel || ""));
                const millimeter = millimeterByIdData.get(String(row?.measurement_millimeter || ""));
                performanceSpaceBySample.set(sampleId, {
                    ...(performanceSpaceBySample.get(sampleId) || {}),
                    width_pixel: pixel?.value ?? null,
                    width_mm: millimeter?.value ?? null,
                });
            });

        return Array.from(sampleIdSet).map((sampleId) => ({
            sample_id: sampleId,
            design_space: designSpaceBySample.get(sampleId) || {},
            performance_space: performanceSpaceBySample.get(sampleId) || {},
        }));
    };

    const buildExplorationSampleIds = (payload) => {
        const sampleIds = new Set(
            (payload.collectionSampleMaps || [])
                .map((entry) => String(entry?.sample || ""))
                .filter(Boolean)
        );
        if (sampleIds.size > 0) return Array.from(sampleIds);
        [payload.laserDesignParameters, payload.colorMeasurementSets, payload.widthMeasurementSets].forEach((rows) => {
            (rows || []).forEach((row) => {
                const sampleId = String(row?.sample || "");
                if (sampleId) sampleIds.add(sampleId);
            });
        });
        return Array.from(sampleIds);
    };

    const handleDownloadExplorationSamplePointsJson = async () => {
        if (isPreparingExplorationExport) return;
        setIsPreparingExplorationExport(true);
        try {
            if (!explorationId) {
                setGenerationLoadError("Missing exploration ID; please reopen exploration from the list.");
                return;
            }
            const response = await axios.post(
                "api/exploration/export/sample-points",
                { explorationId },
                { timeout: 30000 }
            );
            const payload = response?.data || {};
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
            downloadBlob(
                blob,
                `exploration-${explorationId}-sample-points.json`
            );
            if (Array.isArray(payload?.sample_points) && payload.sample_points.length === 0) {
                setGenerationLoadError("Export succeeded but contains 0 samples for this exploration.");
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                // Fallback for backend instances that have not loaded the new export endpoint yet.
                try {
                    let exportData = {
                        collectionSampleMaps,
                        laserDesignParameters,
                        laserDesignParameterTypes,
                        colorMeasurementSets,
                        widthMeasurementSets,
                        measurementRgb,
                        measurementCielab,
                        measurementCiexyz,
                        measurementPixel,
                        measurementMillimeter,
                    };
                    const shouldRefreshGenerationData =
                        exportData.collectionSampleMaps.length === 0 ||
                        (
                            exportData.laserDesignParameters.length === 0 &&
                            exportData.colorMeasurementSets.length === 0 &&
                            exportData.widthMeasurementSets.length === 0
                        );
                    if (shouldRefreshGenerationData) {
                        const refreshed = await getGenerationDataForExport();
                        if (refreshed) {
                            exportData = {
                                collectionSampleMaps: refreshed.collectionSampleMaps,
                                laserDesignParameters: refreshed.laserDesignParameters,
                                laserDesignParameterTypes: refreshed.laserDesignParameterTypes,
                                colorMeasurementSets: refreshed.colorMeasurementSets,
                                widthMeasurementSets: refreshed.widthMeasurementSets,
                                measurementRgb: refreshed.measurementRgb,
                                measurementCielab: refreshed.measurementCielab,
                                measurementCiexyz: refreshed.measurementCiexyz,
                                measurementPixel: refreshed.measurementPixel,
                                measurementMillimeter: refreshed.measurementMillimeter,
                            };
                        }
                    }
                    const explorationSampleIds = buildExplorationSampleIds(exportData);
                    const fallbackPayload = {
                        exported_at: new Date().toISOString(),
                        exploration_name: explorationData?.name || null,
                        exploration_id: explorationData?.id || explorationId,
                        laser_name: explorationData?.laser_device_name || null,
                        substrate_name: explorationData?.material_name || null,
                        iteration_count: allIterations.length,
                        creator: explorationData?.started_by_email || explorationData?.auth_user || null,
                        sample_points: buildSamplePointsForSampleIds(explorationSampleIds, {
                            laserDesignParametersData: exportData.laserDesignParameters,
                            laserDesignParameterTypesData: exportData.laserDesignParameterTypes,
                            colorMeasurementSetsData: exportData.colorMeasurementSets,
                            widthMeasurementSetsData: exportData.widthMeasurementSets,
                            measurementRgbData: exportData.measurementRgb,
                            measurementCielabData: exportData.measurementCielab,
                            measurementCiexyzData: exportData.measurementCiexyz,
                            measurementPixelData: exportData.measurementPixel,
                            measurementMillimeterData: exportData.measurementMillimeter,
                        }),
                    };
                    const fallbackBlob = new Blob([JSON.stringify(fallbackPayload, null, 2)], { type: "application/json" });
                    downloadBlob(
                        fallbackBlob,
                        `exploration-${explorationId}-sample-points.json`
                    );
                } catch (fallbackError) {
                    console.error("Fallback export failed:", fallbackError);
                    setGenerationLoadError("Export endpoint not found and fallback export failed.");
                }
            } else {
                console.error("Export sample points failed:", error);
                setGenerationLoadError("Failed to export sample points JSON.");
            }
        } finally {
            setIsPreparingExplorationExport(false);
        }
    };

    const handleDownloadExplorationMetadataJson = () => {
        const payload = explorationData || {};
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        downloadBlob(
            blob,
            `exploration-${explorationId}-metadata.json`
        );
    };

    const handleDownloadCurrentCollectionJson = async () => {
        if (isPreparingCollectionExport) return;
        setIsPreparingCollectionExport(true);
        try {
            const selectedCollectionSampleIds = membersForSelectedCollection.map((entry) => String(entry?.sample || ""));
            let exportData = {
                laserDesignParameters,
                laserDesignParameterTypes,
                colorMeasurementSets,
                widthMeasurementSets,
                measurementRgb,
                measurementCielab,
                measurementCiexyz,
                measurementPixel,
                measurementMillimeter,
            };
            try {
                // Fetch a complete exploration-scoped snapshot to avoid partial
                // per-member lazy data leaking into collection export files.
                const refreshed = await getGenerationDataForExport();
                if (refreshed) {
                    exportData = {
                        laserDesignParameters: refreshed.laserDesignParameters,
                        laserDesignParameterTypes: refreshed.laserDesignParameterTypes,
                        colorMeasurementSets: refreshed.colorMeasurementSets,
                        widthMeasurementSets: refreshed.widthMeasurementSets,
                        measurementRgb: refreshed.measurementRgb,
                        measurementCielab: refreshed.measurementCielab,
                        measurementCiexyz: refreshed.measurementCiexyz,
                        measurementPixel: refreshed.measurementPixel,
                        measurementMillimeter: refreshed.measurementMillimeter,
                    };
                }
            } catch (error) {
                console.error("Collection export refresh failed; using currently loaded data.", error);
            }

            const payload = {
                exported_at: new Date().toISOString(),
                iteration: selectedIteration,
                collection_role: selectedCollectionRole,
                collection_id: collectionIdForRole || null,
                collection_created_at: selectedCollectionMeta?.created_at || null,
                sample_points: buildSamplePointsForSampleIds(selectedCollectionSampleIds, {
                    laserDesignParametersData: exportData.laserDesignParameters,
                    laserDesignParameterTypesData: exportData.laserDesignParameterTypes,
                    colorMeasurementSetsData: exportData.colorMeasurementSets,
                    widthMeasurementSetsData: exportData.widthMeasurementSets,
                    measurementRgbData: exportData.measurementRgb,
                    measurementCielabData: exportData.measurementCielab,
                    measurementCiexyzData: exportData.measurementCiexyz,
                    measurementPixelData: exportData.measurementPixel,
                    measurementMillimeterData: exportData.measurementMillimeter,
                }),
            };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
            downloadBlob(blob, `iteration-${selectedIteration}-${collectionRoleLabel(selectedCollectionRole).toLowerCase()}-collection.json`);
        } finally {
            setIsPreparingCollectionExport(false);
        }
    };

    return (
        <Stack
            alignItems="center"
            sx={{ mt: "2%", mb: 4, width: "100%" }}
            spacing={2}
        >
            <ExplorationPageShell
                title={pageTitle}
                subtitle={pageSubtitle}
                stepSidebar={pageMode === "iteration" ? {
                    steps: EXPLORATION_FLOW_STEPS,
                    currentIndex: 3,
                    iterationValue: iterationNumber,
                    populationLabel: populationLabelForPhase(iterationPhase, iterationNumber),
                } : null}
                headerTrailing={(
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(prevPage)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Exploration Overview
                    </Button>
                )}
                paperSx={{
                    overflow: "auto",
                    border: "none",
                    boxShadow: "none",
                    p: { xs: 2, md: 2 },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        position: "relative",
                        minHeight: isAnalysePage && isAnalyseInitialDataLoading ? 260 : "auto",
                    }}
                >
                    {isAnalysePage && isAnalyseInitialDataLoading ? (
                        <Stack
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ py: 8 }}
                        >
                            <CircularProgress size={30} />
                            <Typography variant="body2" color="text.secondary">
                                Loading analysis data...
                            </Typography>
                        </Stack>
                    ) : null}
                    <Box sx={{ display: isAnalysePage && isAnalyseInitialDataLoading ? "none" : "block" }}>
                    {!isAnalysePage && explorationCompleted ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Exploration completed.
                        </Alert>
                    ) : null}
                    <Tabs
                        value={safeTabIndex}
                        onChange={(_e, v) => setResultsTab(v)}
                        variant="fullWidth"
                        sx={{
                            mb: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem" },
                        }}
                        aria-label="Results plot"
                    >
                        {tabDefinitions.map((tab) => (
                            <Tab key={tab.key} label={tab.label} />
                        ))}
                    </Tabs>

                    {/* Lineage-load and Sample Points export errors, shown on whichever tab is open. */}
                    {generationLoadError ? (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenerationLoadError("")}>
                            {generationLoadError}
                        </Alert>
                    ) : null}

                    {activeTabKey === "overview" ? (
                        <Box sx={resultPanelSx}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                    gap: 1,
                                    mb: 1.5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        gridColumn: "1 / -1",
                                        display: "block",
                                        width: "100%",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    <strong>Name:</strong> {String(explorationData?.name || "n/a").replace(/\s*\n+\s*/g, " ")}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        gridColumn: "1 / -1",
                                        display: "block",
                                        width: "100%",
                                    }}
                                >
                                    <strong>Description:</strong> {explorationData?.description || "n/a"}
                                </Typography>
                                <Typography variant="body2"><strong>Objective:</strong> {objectiveName || "n/a"}</Typography>
                                <Typography variant="body2" data-testid="exploration-start-mode">
                                    <strong>Start:</strong> {START_MODE_LABELS[explorationData?.start_mode] || "n/a"}
                                </Typography>
                                <Typography variant="body2"><strong>Creator:</strong> {explorationData?.started_by_email || explorationData?.auth_user || "n/a"}</Typography>
                                <Typography variant="body2"><strong>Created at:</strong> {explorationData?.created_at ? new Date(explorationData.created_at).toLocaleString() : "n/a"}</Typography>
                                <Typography variant="body2"><strong>Laser:</strong> {explorationData?.laser_device_name || "n/a"}</Typography>
                                <Typography variant="body2"><strong>Substrate:</strong> {explorationData?.material_name || "n/a"}</Typography>
                                <Typography variant="body2" data-testid="exploration-prepass">
                                    <strong>Prepass:</strong>{" "}
                                    {explorationData?.prepass_sample
                                        ? [
                                              explorationData?.prepass_sample_info?.sample_id || explorationData.prepass_sample,
                                              explorationData?.prepass_sample_info?.exploration_name,
                                          ].filter(Boolean).join(" · ")
                                        : "none"}
                                </Typography>
                                <Typography variant="body2"><strong>Current iteration:</strong> {iterationNumber}</Typography>
                                <Typography variant="body2"><strong>Iterations with data:</strong> {allIterations.length}</Typography>
                                <Typography variant="body2">
                                    <strong>Sample Points:</strong>{" "}
                                    <MuiTooltip
                                        title={
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                                <Typography variant="caption">Width: {widthData.length}</Typography>
                                                <Typography variant="caption">Color: {colorData.length}</Typography>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Box component="span" sx={{ cursor: "help", textDecoration: "underline dotted" }}>
                                            {samplePointsCount}
                                        </Box>
                                    </MuiTooltip>
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadExplorationSamplePointsJson}
                                    disabled={isPreparingExplorationExport}
                                    sx={{ textTransform: "none" }}
                                >
                                    {isPreparingExplorationExport ? "Preparing Sample Points..." : "Sample Points"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadExplorationMetadataJson}
                                    disabled={!explorationData}
                                    sx={{ textTransform: "none" }}
                                >
                                    Exploration Metadata
                                </Button>
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTabKey === "width" ? (
                        <Box sx={resultPanelSx}>
                            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                <BinBarChart
                                    data={widthResults}
                                    width={CHART_WIDTH}
                                    height={CHART_HEIGHT}
                                    margin={BIN_CHART_MARGIN}
                                />
                            </Box>
                            <Box
                                sx={{
                                    minHeight: showWidthSlider ? SLIDER_ROW_MIN_HEIGHT : 0,
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mt: showWidthSlider ? 1 : 0.25,
                                }}
                            >
                                {showWidthSlider ? (
                                    <Slider
                                        aria-label="Iteration"
                                        value={widthSliderValue}
                                        onChange={handleWidthSliderChange}
                                        valueLabelDisplay="auto"
                                        step={1}
                                        marks
                                        min={0}
                                        max={iterationNumber}
                                        sx={{ width: 360, maxWidth: "100%" }}
                                    />
                                ) : null}
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                value={selectedValueWidth}
                                onChange={handleWidthScopeChange}
                                aria-label="Width results scope"
                                size="small"
                                sx={scopeToggleSx}
                            >
                                <ToggleButton value="current">Current</ToggleButton>
                                <ToggleButton value="cumulative">Cumulative</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    ) : null}

                    {activeTabKey === "color" ? (
                        <Box sx={resultPanelSx}>
                            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                <MyScatterChart
                                    dataColor={colorResults}
                                    domainData={colorData}
                                    width={CHART_WIDTH}
                                    height={CHART_HEIGHT}
                                    margin={CHART_MARGIN}
                                />
                            </Box>
                            <Box
                                sx={{
                                    minHeight: showColorSlider ? SLIDER_ROW_MIN_HEIGHT : 0,
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mt: showColorSlider ? 1 : 0.25,
                                }}
                            >
                                {showColorSlider ? (
                                    <Slider
                                        aria-label="Iteration"
                                        value={colorSliderValue}
                                        onChange={handleColorSliderChange}
                                        valueLabelDisplay="auto"
                                        step={1}
                                        marks
                                        min={0}
                                        max={iterationNumber}
                                        sx={{ width: 360, maxWidth: "100%" }}
                                    />
                                ) : null}
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                value={selectedValueColor}
                                onChange={handleColorScopeChange}
                                aria-label="Color results scope"
                                size="small"
                                sx={scopeToggleSx}
                            >
                                <ToggleButton value="current">Current</ToggleButton>
                                <ToggleButton value="cumulative">Cumulative</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    ) : null}

                    {activeTabKey === "widthGrowth" ? (
                        <Box sx={resultPanelSx}>
                            <Stack spacing={1.5}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Min width"
                                        value={Number.isFinite(evolutionMin) ? evolutionMin : ""}
                                        onChange={(event) => {
                                            const raw = Number(event.target.value);
                                            if (!Number.isFinite(raw)) return;
                                            const clamped = Math.max(evolutionAllowedMin, Math.min(raw, evolutionAllowedMax));
                                            const proposedMin = roundTo3(clamped);
                                            if (proposedMin >= evolutionMax) {
                                                const fallbackMin = Math.max(evolutionAllowedMin, roundTo3(evolutionMax - 0.001));
                                                setWidthGrowthMin(roundTo3(fallbackMin));
                                                return;
                                            }
                                            setWidthGrowthMin(proposedMin);
                                        }}
                                        inputProps={{
                                            min: evolutionAllowedMin,
                                            max: evolutionAllowedMax,
                                            step: 0.001,
                                        }}
                                        helperText={`Range floor: ${formatTo3(evolutionAllowedMin)}`}
                                        sx={{ width: { xs: "100%", md: 180 } }}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Max width"
                                        value={Number.isFinite(evolutionMax) ? evolutionMax : ""}
                                        onChange={(event) => {
                                            const raw = Number(event.target.value);
                                            if (!Number.isFinite(raw)) return;
                                            const clamped = Math.max(evolutionAllowedMin, Math.min(raw, evolutionAllowedMax));
                                            const proposedMax = roundTo3(clamped);
                                            if (proposedMax <= evolutionMin) {
                                                const fallbackMax = Math.min(evolutionAllowedMax, roundTo3(evolutionMin + 0.001));
                                                setWidthGrowthMax(roundTo3(fallbackMax));
                                                return;
                                            }
                                            setWidthGrowthMax(proposedMax);
                                        }}
                                        inputProps={{
                                            min: evolutionAllowedMin,
                                            max: evolutionAllowedMax,
                                            step: 0.001,
                                        }}
                                        helperText={`Range ceiling: ${formatTo3(evolutionAllowedMax)}`}
                                        sx={{ width: { xs: "100%", md: 180 } }}
                                    />
                                </Stack>
                                {!evolutionRangeValid ? (
                                    <Alert severity="warning">
                                        Please keep min width smaller than max width and within the global width bounds.
                                    </Alert>
                                ) : null}
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    {evolutionChartData.length > 0 && evolutionRangeValid ? (
                                        <LineChart
                                            width={CHART_WIDTH}
                                            height={CHART_HEIGHT}
                                            data={evolutionChartData}
                                            margin={CHART_MARGIN}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis
                                                dataKey="iteration"
                                                type="number"
                                                allowDecimals={false}
                                                tick={{ fontSize: 11 }}
                                                stroke="#94a3b8"
                                                label={{
                                                    value: "Iteration number",
                                                    angle: 0,
                                                    position: "insideBottom",
                                                    offset: -10,
                                                    fill: "#64748b",
                                                    fontSize: 12,
                                                }}
                                            />
                                            <YAxis
                                                dataKey="average_value"
                                                tickFormatter={(value) => formatTo3(value)}
                                                tick={{ fontSize: 11 }}
                                                stroke="#94a3b8"
                                                label={{
                                                    value: evolutionYAxisLabel,
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    fill: "#64748b",
                                                    fontSize: 12,
                                                }}
                                            />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                                                formatter={(value) => formatTo3(value)}
                                                labelFormatter={(label) => `Iteration ${label}`}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="average_value"
                                                stroke="#0ea5e9"
                                                strokeWidth={2.5}
                                                dot={{ r: 3 }}
                                                activeDot={{ r: 5 }}
                                            />
                                        </LineChart>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: CHART_WIDTH,
                                                maxWidth: "100%",
                                                height: CHART_HEIGHT,
                                                borderRadius: 2,
                                                bgcolor: "action.hover",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "text.secondary",
                                                typography: "body2",
                                                textAlign: "center",
                                                px: 2,
                                            }}
                                        >
                                            {evolutionNoDataText}
                                        </Box>
                                    )}
                                </Box>
                                <ToggleButtonGroup
                                    exclusive
                                    value={evolutionMetric}
                                    onChange={(_event, newValue) => {
                                        if (!newValue) return;
                                        setEvolutionMetric(newValue);
                                    }}
                                    aria-label="Evolution metric switch"
                                    size="small"
                                    sx={scopeToggleSx}
                                >
                                    <ToggleButton value="width">Width</ToggleButton>
                                    <ToggleButton value="chromaticity">Chromaticity</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTabKey === "validSamples" ? (
                        <Box sx={resultPanelSx}>
                            <Stack spacing={1.5} alignItems="center">
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Max width (µm)"
                                        value={validMaxWidthText}
                                        onChange={(event) => setValidMaxWidthText(event.target.value)}
                                        error={validThresholds.maximumWidthUm === null}
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ width: { xs: "100%", md: 160 } }}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Max marking time (s/cm²)"
                                        value={validTimeText}
                                        onChange={(event) => setValidTimeText(event.target.value)}
                                        error={!validTime.ok}
                                        helperText="Empty: no limit"
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ width: { xs: "100%", md: 200 } }}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Line count"
                                        value={validLineCountText}
                                        onChange={(event) => setValidLineCountText(event.target.value)}
                                        error={!validLineCount.ok}
                                        helperText="Empty: any"
                                        inputProps={{ min: 1, step: 1 }}
                                        sx={{ width: { xs: "100%", md: 130 } }}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={validKind === "chromatic" ? "Min chromaticity" : "Max chromaticity"}
                                        value={validChromaticityText}
                                        onChange={(event) => setValidChromaticityText(event.target.value)}
                                        error={validThresholds.chromaticityThreshold === null}
                                        inputProps={{ min: 0, step: 1 }}
                                        sx={{ width: { xs: "100%", md: 180 } }}
                                    />
                                    {validKind !== "chromatic" ? (
                                        <TextField
                                            size="small"
                                            type="number"
                                            label={validKind === "white" ? "Min L*" : "Max L*"}
                                            value={validLightnessText}
                                            onChange={(event) => setValidLightnessText(event.target.value)}
                                            error={validThresholds.lightnessThreshold === null}
                                            inputProps={{ min: 0, max: 100, step: 1 }}
                                            sx={{ width: { xs: "100%", md: 180 } }}
                                        />
                                    ) : null}
                                </Stack>
                                {!validThresholdsOk ? (
                                    <Alert severity="warning">Enter numbers of 0 or more. Line count: a whole number of 1 or more, or empty.</Alert>
                                ) : null}
                                {validSamplesError ? <Alert severity="error">{validSamplesError}</Alert> : null}
                                {validGamutSamples.length > 0 ? (
                                    <>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {`Through iteration ${validGamutValue}: ${validGamutValidCount} valid of ${validGamutSamples.length} measured`}
                                        </Typography>
                                        {validSampleIterations.length > 1 ? (
                                            <Slider
                                                aria-label="Gamut iteration"
                                                value={validGamutValue}
                                                onChange={(_event, value) => setValidGamutIteration(Number(value))}
                                                valueLabelDisplay="auto"
                                                step={1}
                                                marks
                                                min={validSampleIterations[0]}
                                                max={validGamutMax}
                                                sx={{ width: 360, maxWidth: "100%" }}
                                            />
                                        ) : null}
                                        <ValidSamplesGamut samples={validGamutSamples} rangeSamples={validSamplePoints} />
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            width: CHART_WIDTH,
                                            maxWidth: "100%",
                                            height: CHART_HEIGHT,
                                            borderRadius: 2,
                                            bgcolor: "action.hover",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "text.secondary",
                                            typography: "body2",
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        No measured iteration yet.
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTabKey === "designPerformance" ? (
                        <Box sx={{ ...resultPanelSx, maxWidth: "100%" }}>
                            {isGenerationDataLoading && !hasLoadedGenerationData ? (
                                <Stack spacing={1} alignItems="center" sx={{ py: 5 }}>
                                    <CircularProgress size={26} />
                                    <Typography variant="body2" color="text.secondary">
                                        Loading design and performance data...
                                    </Typography>
                                </Stack>
                            ) : (
                            <Stack spacing={2}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                                    gap: 2,
                                    alignItems: "stretch",
                                }}
                            >
                                <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                        <FormControl size="small" sx={{ minWidth: 190, flex: 1 }}>
                                            <InputLabel id="analysis-iteration-select-label">Iteration</InputLabel>
                                            <Select
                                                labelId="analysis-iteration-select-label"
                                                label="Iteration"
                                                value={selectedIteration}
                                                onChange={(event) => setAnalysisIteration(Number(event.target.value))}
                                            >
                                                {allIterations.length === 0 ? (
                                                    <MenuItem value={0} disabled>
                                                        No iterations
                                                    </MenuItem>
                                                ) : null}
                                                {allIterations.map((iter) => (
                                                    <MenuItem key={iter} value={iter}>
                                                        Iteration {iter}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <FormControl size="small" sx={{ minWidth: 190, flex: 1 }}>
                                            <InputLabel id="collection-role-select-label">Collection</InputLabel>
                                            <Select
                                                labelId="collection-role-select-label"
                                                label="Collection"
                                                value={selectedCollectionRole}
                                                onChange={(event) => setSelectedCollectionRole(String(event.target.value))}
                                            >
                                                <MenuItem value="input">Input</MenuItem>
                                                <MenuItem value="combined">Generated</MenuItem>
                                                <MenuItem value="output">Output</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>

                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 2.5,
                                            borderColor: "divider",
                                            boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                                            flex: 1,
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.25 }}>
                                            <Stack spacing={1.5}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    Collection details
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <Chip size="small" variant="outlined" label={`Iteration ${selectedIteration}`} />
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`${collectionRoleLabel(selectedCollectionRole)} collection`}
                                                    />
                                                    <Chip size="small" variant="outlined" label={`${membersForSelectedCollection.length} members`} />
                                                </Stack>
                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2"><strong>Collection ID:</strong> {collectionIdForRole || "n/a"}</Typography>
                                                    <Typography variant="body2"><strong>Collection created:</strong> {selectedCollectionMeta?.created_at ? new Date(selectedCollectionMeta.created_at).toLocaleString() : "n/a"}</Typography>
                                                    <Typography variant="body2"><strong>Iteration width points:</strong> {widthForSelectedIteration.length}</Typography>
                                                    <Typography variant="body2"><strong>Iteration color points:</strong> {colorForSelectedIteration.length}</Typography>
                                                </Box>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                                                    onClick={handleDownloadCurrentCollectionJson}
                                                    startIcon={
                                                        isPreparingCollectionExport ? (
                                                            <CircularProgress size={14} />
                                                        ) : (
                                                            <DownloadIcon />
                                                        )
                                                    }
                                                    disabled={isPreparingCollectionExport}
                                                >
                                                    {isPreparingCollectionExport ? "Preparing..." : "JSON"}
                                                </Button>

                                                {collectionMarking && collectionMarking.markings.some((marking) => marking.origin?.is_own_collection) ? (
                                                    <>
                                                        <Divider />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            Markings and captured images
                                                        </Typography>
                                                        {collectionMarking.markings.filter((marking) => marking.origin?.is_own_collection).map((marking) => {
                                                            const capturedPasses = marking.passes.filter((entry) => entry.source_image);
                                                            const originLabel = marking.origin?.label
                                                                || (marking.origin?.iteration_number !== null && marking.origin?.iteration_number !== undefined
                                                                    ? `Iteration ${marking.origin.iteration_number}`
                                                                    : "Unknown iteration");
                                                            return (
                                                                <Box key={marking.patch_group_id} sx={{ mt: 1 }}>
                                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                                                                        <Chip size="small" variant="outlined" label={originLabel} />
                                                                        <Chip size="small" variant="outlined" label={`Batch ${marking.batch_number}`} />
                                                                        <Chip
                                                                            size="small"
                                                                            variant="outlined"
                                                                            label={`${marking.member_count} member${marking.member_count === 1 ? "" : "s"}`}
                                                                        />
                                                                    </Stack>
                                                                    {capturedPasses.length === 0 ? (
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Marked, but nothing photographed yet.
                                                                        </Typography>
                                                                    ) : (
                                                                        <Box
                                                                            sx={{
                                                                                display: "grid",
                                                                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                                                                gap: 2,
                                                                            }}
                                                                        >
                                                                            {capturedPasses.map((entry) => (
                                                                                <CaptureImage
                                                                                    key={`${marking.patch_group_id}-${entry.pass_kind}`}
                                                                                    explorationId={explorationId}
                                                                                    plateId={entry.plate_id}
                                                                                    patchGroupId={marking.patch_group_id}
                                                                                    passKind={entry.pass_kind}
                                                                                    sourceImage={entry.source_image}
                                                                                    caption={`${entry.pass_kind === "COLOR" ? "Color pass" : "Cluster pass"}${entry.plate_name ? ` - ${entry.plate_name}` : ""}`}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </>
                                                ) : null}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Stack>

                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2.5,
                                        borderColor: "divider",
                                        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <CardContent sx={{ p: 2.25, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                                        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {showPhysicalPatchGroups ? "Marked patch group" : "Member origins"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                {showPhysicalPatchGroups
                                                    ? "As marked on the plate. Click a patch to inspect its sample point."
                                                    : `One column per iteration the members were marked in, stacked by rank (best at the bottom)${memberOriginIterationCount > 0 ? ` - ${membersForSelectedCollection.length} members from ${memberOriginIterationCount} iteration${memberOriginIterationCount === 1 ? "" : "s"}` : ""}. Click a patch to inspect it.`}
                                            </Typography>

                                            {isLoadingCollectionMarking || isLoadingMarkingPatchGrids ? (
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <CircularProgress size={18} />
                                                    <Typography variant="body2">Loading patch groups...</Typography>
                                                </Stack>
                                            ) : null}

                                            {showPhysicalPatchGroups ? (
                                                <PhysicalPatchGroups
                                                    markings={ownMarkingsWithGrid}
                                                    gridsByPatchGroup={markingPatchGrids}
                                                    selectedSampleId={selectedMemberId}
                                                    isSampleSelectable={(sampleId) => memberSampleIdSet.has(sampleId)}
                                                    onSelectSampleId={(sampleId) => setSelectedMemberId(sampleId)}
                                                />
                                            ) : null}

                                            {!showPhysicalPatchGroups && markingsWithGrid.length > 0 ? (
                                                <MemberOriginColumns
                                                    members={memberOriginEntries}
                                                    selectedSampleId={selectedMemberId}
                                                    onSelectSampleId={(sampleId) => setSelectedMemberId(sampleId)}
                                                />
                                            ) : null}

                                            {!isLoadingCollectionMarking && !isLoadingMarkingPatchGrids && markingsWithGrid.length === 0 ? (
                                                <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
                                                    No marked patch group found for the members of this collection.
                                                </Typography>
                                            ) : null}

                                            <Divider />
                                            <FormControl size="small" sx={{ minWidth: 260, maxWidth: 420 }}>
                                                <InputLabel id="collection-member-select-label">Member</InputLabel>
                                                <Select
                                                    labelId="collection-member-select-label"
                                                    label="Member"
                                                    value={selectedMemberId}
                                                    onChange={(event) => setSelectedMemberId(String(event.target.value))}
                                                >
                                                    {membersForSelectedCollection.length === 0 ? (
                                                        <MenuItem value="" disabled>
                                                            No members in this collection
                                                        </MenuItem>
                                                    ) : null}
                                                    {membersForSelectedCollection.map((entry, index) => (
                                                        <MenuItem key={entry.id || entry.sample || index} value={String(entry.sample)}>
                                                            {`#${index + 1} - ${String(entry.sample).slice(0, 8)}${entry.rank != null ? ` (rank ${entry.rank})` : ""}`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Box>

                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 2.5,
                                            borderColor: "divider",
                                            boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.25 }}>
                                            {selectedMemberEntry ? (
                                                <Stack spacing={1.5}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        Sample point details
                                                    </Typography>
                                                    {isMemberDataLoading ? (
                                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                            Refreshing sample details...
                                                        </Typography>
                                                    ) : null}

                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Typography variant="body2"><strong>Sample ID:</strong> {selectedMemberId}</Typography>
                                                        <Typography variant="body2">
                                                            <strong>Comes from:</strong>{" "}
                                                            {memberOrigin
                                                                ? `Iteration ${memberOrigin.iteration} (${memberOrigin.label})`
                                                                : "Unknown"}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <strong>Rank in this collection:</strong>{" "}
                                                            {selectedMemberEntry?.rank ?? "-"}
                                                        </Typography>
                                                        {memberMarkingPosition ? (
                                                            <Typography variant="body2">
                                                                <strong>Position in marking:</strong>{" "}
                                                                {`Row ${memberMarkingPosition.row}, Column ${memberMarkingPosition.column}`}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>

                                                    <Divider />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Design parameters
                                                    </Typography>
                                                    {designParametersForMember.length > 0 ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {designParametersForMember.map((parameter) => (
                                                                <Chip
                                                                    key={parameter.id || parameter.key}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={`${formatParameterLabel(parameter.key)}: ${formatTo3(parameter.value)}`}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                            No design parameter values found for this sample in the loaded data.
                                                        </Typography>
                                                    )}

                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Color measurements
                                                    </Typography>
                                                    {colorMeasurementsForMember.length > 0 ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {colorMeasurementsForMember.map((measurement, index) => (
                                                                <Stack key={measurement.id || index} direction="row" spacing={0.75}>
                                                                    <MuiTooltip
                                                                        arrow
                                                                        title={`Measurement ID: ${measurement.id || "n/a"} (CIELAB)`}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 108,
                                                                                p: 0.75,
                                                                                border: "1px solid",
                                                                                borderColor: "divider",
                                                                                borderRadius: 1.5,
                                                                                cursor: "default",
                                                                            }}
                                                                        >
                                                                            {(() => {
                                                                                const previewRgb = labToDisplayRgb(measurement.cielab);
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: "100%",
                                                                                            height: 34,
                                                                                            borderRadius: 1,
                                                                                            border: "1px solid",
                                                                                            borderColor: "divider",
                                                                                            bgcolor: previewRgb || "transparent",
                                                                                            backgroundImage: previewRgb ? "none" : "repeating-linear-gradient(45deg, #e5e7eb 0, #e5e7eb 4px, #f8fafc 4px, #f8fafc 8px)",
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            })()}
                                                                            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                                                CIELAB
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.cielab
                                                                                    ? `L* ${formatTo3(measurement.cielab.l)}`
                                                                                    : "L* n/a"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.cielab
                                                                                    ? `a* ${formatTo3(measurement.cielab.a)}`
                                                                                    : "a* n/a"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.cielab
                                                                                    ? `b* ${formatTo3(measurement.cielab.b)}`
                                                                                    : "b* n/a"}
                                                                            </Typography>
                                                                        </Box>
                                                                    </MuiTooltip>

                                                                    <MuiTooltip
                                                                        arrow
                                                                        title={`Measurement ID: ${measurement.id || "n/a"} (CIEXYZ)`}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 108,
                                                                                p: 0.75,
                                                                                border: "1px solid",
                                                                                borderColor: "divider",
                                                                                borderRadius: 1.5,
                                                                                cursor: "default",
                                                                            }}
                                                                        >
                                                                            {(() => {
                                                                                // every card shows the CIELAB colour; only the listed values differ
                                                                                const previewRgb = labToDisplayRgb(measurement.cielab);
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: "100%",
                                                                                            height: 34,
                                                                                            borderRadius: 1,
                                                                                            border: "1px solid",
                                                                                            borderColor: "divider",
                                                                                            bgcolor: previewRgb || "transparent",
                                                                                            backgroundImage: previewRgb ? "none" : "repeating-linear-gradient(45deg, #e5e7eb 0, #e5e7eb 4px, #f8fafc 4px, #f8fafc 8px)",
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            })()}
                                                                            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                                                CIEXYZ
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.ciexyz
                                                                                    ? `X ${formatTo3(measurement.ciexyz.x)}`
                                                                                    : "X n/a"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.ciexyz
                                                                                    ? `Y ${formatTo3(measurement.ciexyz.y)}`
                                                                                    : "Y n/a"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.ciexyz
                                                                                    ? `Z ${formatTo3(measurement.ciexyz.z)}`
                                                                                    : "Z n/a"}
                                                                            </Typography>
                                                                        </Box>
                                                                    </MuiTooltip>

                                                                    <MuiTooltip
                                                                        arrow
                                                                        title={`Measurement ID: ${measurement.id || "n/a"} (RGB)`}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 108,
                                                                                p: 0.75,
                                                                                border: "1px solid",
                                                                                borderColor: "divider",
                                                                                borderRadius: 1.5,
                                                                                cursor: "default",
                                                                            }}
                                                                        >
                                                                            {(() => {
                                                                                // the camera reading is listed, never painted
                                                                                const previewRgb = labToDisplayRgb(measurement.cielab);
                                                                                return (
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: "100%",
                                                                                            height: 34,
                                                                                            borderRadius: 1,
                                                                                            border: "1px solid",
                                                                                            borderColor: "divider",
                                                                                            bgcolor: previewRgb || "transparent",
                                                                                            backgroundImage: previewRgb ? "none" : "repeating-linear-gradient(45deg, #e5e7eb 0, #e5e7eb 4px, #f8fafc 4px, #f8fafc 8px)",
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            })()}
                                                                            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                                                RGB
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.rgb
                                                                                    ? `R ${formatTo3(measurement.rgb.r)}`
                                                                                    : "R n/a"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.rgb
                                                                                    ? `G ${formatTo3(measurement.rgb.g)}`
                                                                                    : "G n/a"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ display: "block" }}>
                                                                                {measurement.rgb
                                                                                    ? `B ${formatTo3(measurement.rgb.b)}`
                                                                                    : "B n/a"}
                                                                            </Typography>
                                                                        </Box>
                                                                    </MuiTooltip>
                                                                </Stack>
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                            No color measurement entries found for this member.
                                                        </Typography>
                                                    )}

                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Width measurements
                                                    </Typography>
                                                    {widthMeasurementsForMember.length > 0 ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {widthMeasurementsForMember.map((measurement, index) => (
                                                                <React.Fragment key={measurement.id || index}>
                                                                    {measurement.measurement_millimeter_value != null ? (
                                                                        <MuiTooltip
                                                                            arrow
                                                                            title={`Measurement ID: ${measurement.measurement_millimeter_id || measurement.id || "n/a"}`}
                                                                        >
                                                                            <Chip
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label={`${formatTo3(measurement.measurement_millimeter_value)} mm`}
                                                                                sx={{ cursor: "default" }}
                                                                            />
                                                                        </MuiTooltip>
                                                                    ) : null}
                                                                    {measurement.measurement_pixel_value != null ? (
                                                                        <MuiTooltip
                                                                            arrow
                                                                            title={`Measurement ID: ${measurement.measurement_pixel_id || measurement.id || "n/a"}`}
                                                                        >
                                                                            <Chip
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label={`${formatTo3(measurement.measurement_pixel_value)} px`}
                                                                                sx={{ cursor: "default" }}
                                                                            />
                                                                        </MuiTooltip>
                                                                    ) : null}
                                                                </React.Fragment>
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                            No width measurement entries found for this member.
                                                        </Typography>
                                                    )}

                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Sample patch images
                                                    </Typography>
                                                    {samplePatchEntriesForMember.length > 0 ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {samplePatchEntriesForMember.map((entry, index) => (
                                                                <MuiTooltip
                                                                    key={`${entry.id}-${index}`}
                                                                    arrow
                                                                    title={`${entry.type} ID: ${entry.id}`}
                                                                >
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        clickable
                                                                        onClick={() => openSamplePatchImage(entry)}
                                                                        onDelete={() => openSamplePatchImage(entry)}
                                                                        deleteIcon={<OpenInNewIcon fontSize="small" />}
                                                                        avatar={
                                                                            samplePatchImageUrls[entry.id] ? (
                                                                                <Avatar
                                                                                    variant="rounded"
                                                                                    src={samplePatchImageUrls[entry.id]}
                                                                                    alt={entry.type}
                                                                                    sx={{ width: 22, height: 22 }}
                                                                                />
                                                                            ) : (
                                                                                <Avatar variant="rounded" sx={{ width: 22, height: 22 }}>
                                                                                    {entry.type.startsWith("Color") ? "C" : "W"}
                                                                                </Avatar>
                                                                            )
                                                                        }
                                                                        label={entry.type}
                                                                            sx={{
                                                                                "& .MuiChip-avatar": {
                                                                                    ml: 0.5,
                                                                                    mr: 0.25,
                                                                                },
                                                                            }}
                                                                    />
                                                                </MuiTooltip>
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                            No patch images available for this sample.
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                    Select an iteration and collection, then click a patch to inspect a sample point.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                            </Stack>
                            )}
                        </Box>
                    ) : null}

                    {activeTabKey === "whiteBlack" && blackWhiteExploration ? (
                        <Box sx={resultPanelSx}>
                            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                <MyScatterChartWhiteBlack
                                    data={whiteBlackColorResults}
                                    domainData={blackWhitecolorData}
                                    width={CHART_WIDTH}
                                    height={CHART_HEIGHT}
                                    margin={CHART_MARGIN}
                                />
                            </Box>
                            <Box
                                sx={{
                                    minHeight: showBlackWhiteSlider ? SLIDER_ROW_MIN_HEIGHT : 0,
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mt: showBlackWhiteSlider ? 1 : 0.25,
                                }}
                            >
                                {showBlackWhiteSlider ? (
                                    <Slider
                                        aria-label="Iteration"
                                        value={whiteBlackColorSliderValue}
                                        onChange={handleBlackWhiteColorSliderChange}
                                        valueLabelDisplay="auto"
                                        step={1}
                                        marks
                                        min={0}
                                        max={iterationNumber}
                                        sx={{ width: 360, maxWidth: "100%" }}
                                    />
                                ) : null}
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                value={selectedValueWhiteBlackColor}
                                onChange={handleBlackWhiteScopeChange}
                                aria-label="Black and white color results scope"
                                size="small"
                                sx={scopeToggleSx}
                            >
                                <ToggleButton value="current">Current</ToggleButton>
                                <ToggleButton value="cumulative">Cumulative</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    ) : null}

                    {activeTabKey === "generations" ? (
                        <Box sx={resultPanelSx}>
                            <Stack spacing={1.5}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Generation browser
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    Inspect each generation's parent pool, generated offspring, and inferred promoted/killed counts.
                                </Typography>

                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                    <FormControl size="small" sx={{ minWidth: 190 }}>
                                        <InputLabel id="generation-iteration-select-label">Generation</InputLabel>
                                        <Select
                                            labelId="generation-iteration-select-label"
                                            label="Generation"
                                            value={selectedGeneration?.iteration_number ?? 0}
                                            onChange={(event) => setAnalysisIteration(Number(event.target.value))}
                                        >
                                            {generationRows.length === 0 ? (
                                                <MenuItem value={0} disabled>
                                                    No generation data
                                                </MenuItem>
                                            ) : null}
                                            {generationRows.map((row) => (
                                                <MenuItem key={row.iteration_number} value={row.iteration_number}>
                                                    Iteration {row.iteration_number}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" sx={{ minWidth: 190 }}>
                                        <InputLabel id="generation-collection-role-select-label">Collection</InputLabel>
                                        <Select
                                            labelId="generation-collection-role-select-label"
                                            label="Collection"
                                            value={generationCollectionRole}
                                            onChange={(event) => setGenerationCollectionRole(String(event.target.value))}
                                        >
                                            <MenuItem value="input">Input</MenuItem>
                                            <MenuItem value="combined">Generated</MenuItem>
                                            <MenuItem value="output">Output</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" sx={{ minWidth: 260 }}>
                                        <InputLabel id="generation-member-select-label">Member</InputLabel>
                                        <Select
                                            labelId="generation-member-select-label"
                                            label="Member"
                                            value={generationSelectedMemberId}
                                            onChange={(event) => setGenerationSelectedMemberId(String(event.target.value))}
                                        >
                                            {membersForSelectedGenerationCollection.length === 0 ? (
                                                <MenuItem value="" disabled>
                                                    No members in this collection
                                                </MenuItem>
                                            ) : null}
                                            {membersForSelectedGenerationCollection.map((entry, index) => (
                                                <MenuItem key={entry.id || entry.sample || index} value={String(entry.sample)}>
                                                    {`#${index + 1} - ${String(entry.sample).slice(0, 8)}${entry.rank != null ? ` (rank ${entry.rank})` : ""}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                {selectedGeneration ? (
                                    <>
                                        <Divider />
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                                gap: 1,
                                            }}
                                        >
                                            <Typography variant="body2"><strong>Iteration:</strong> {selectedGeneration.iteration_number}</Typography>
                                            <Typography variant="body2"><strong>Parents (input):</strong> {selectedGeneration.parents_count}</Typography>
                                            <Typography variant="body2"><strong>Children (output):</strong> {selectedGeneration.children_count}</Typography>
                                            <Typography variant="body2"><strong>Promoted:</strong> {selectedGeneration.promoted_count}</Typography>
                                            <Typography variant="body2"><strong>Killed:</strong> {selectedGeneration.killed_count}</Typography>
                                        </Box>

                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                                            Collection references
                                        </Typography>
                                        <Box
                                            component="pre"
                                            sx={{
                                                m: 0,
                                                p: 1.5,
                                                borderRadius: 1,
                                                bgcolor: "grey.100",
                                                fontSize: 12,
                                                overflow: "auto",
                                            }}
                                        >
                                            {JSON.stringify(
                                                {
                                                    input_collection: selectedGeneration.input_collection,
                                                    generated_collection: selectedGeneration.generated_collection,
                                                    output_collection: selectedGeneration.output_collection,
                                                },
                                                null,
                                                2
                                            )}
                                        </Box>

                                        <Card
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2.5,
                                                borderColor: "divider",
                                                boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                                            }}
                                        >
                                            <CardContent sx={{ p: 2.25 }}>
                                                {selectedGenerationMemberEntry ? (
                                                    <Stack spacing={1.5}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            Sample point details
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            <Chip size="small" label={`Iteration ${selectedGeneration.iteration_number}`} />
                                                            <Chip
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                label={`${collectionRoleLabel(generationCollectionRole)} collection`}
                                                            />
                                                            <Chip size="small" variant="outlined" label={generationMemberLifecycle} />
                                                            {selectedGenerationMemberEntry.rank != null ? (
                                                                <Chip size="small" variant="outlined" label={`Rank ${selectedGenerationMemberEntry.rank}`} />
                                                            ) : null}
                                                        </Stack>
                                                        <Box
                                                            sx={{
                                                                display: "grid",
                                                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography variant="body2"><strong>Sample ID:</strong> {generationSelectedMemberId}</Typography>
                                                            <Typography variant="body2"><strong>Collection ID:</strong> {generationCollectionIdForRole || "n/a"}</Typography>
                                                            <Typography variant="body2"><strong>Collection created:</strong> {selectedGenerationCollectionMeta?.created_at ? new Date(selectedGenerationCollectionMeta.created_at).toLocaleString() : "n/a"}</Typography>
                                                        </Box>
                                                        <Divider />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            Design parameters
                                                        </Typography>
                                                        {generationDesignParametersForMember.length > 0 ? (
                                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                                {generationDesignParametersForMember.map((parameter) => (
                                                                    <Chip
                                                                        key={parameter.id || parameter.key}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={`${formatParameterLabel(parameter.key)}: ${formatTo3(parameter.value)}`}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        ) : (
                                                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                                No design parameter values found for this sample in the loaded data.
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                        Select a generation, collection, and member to inspect a sample point.
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </>
                                ) : (
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        No generation records are available for this exploration yet.
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTabKey === "plates" ? (
                        <Box sx={resultPanelSx}>
                            <Stack spacing={1.5}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Plates
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    Track which physical plates are in use, which slots they
                                    occupy by iteration / batch / phase, and download a plate-
                                    label marking file for the lab.
                                </Typography>
                                <PlatesView explorationId={explorationId} />
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTabKey === "configs" ? (
                        <Box sx={resultPanelSx}>
                            <Stack spacing={1.5}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Configurations
                                </Typography>
                                <ExplorationConfigsPanel
                                    explorationId={explorationId}
                                />
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTabKey === "nextGeneration" ? (
                        <Box sx={resultPanelSx} data-testid="next-generation-panel">
                            <Stack spacing={1.5}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Next generation
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    Next keeps the best measured samples and prepares the next population to mark.
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label="Genetic algorithm"
                                        variant={nextGenerationSource === "optimizer" ? "filled" : "outlined"}
                                        color={nextGenerationSource === "optimizer" ? "primary" : "default"}
                                        onClick={() => handleNextGenerationSource("optimizer")}
                                        sx={{ cursor: "pointer" }}
                                    />
                                    <Chip
                                        label="Upload population"
                                        variant={nextGenerationSource === "upload" ? "filled" : "outlined"}
                                        color={nextGenerationSource === "upload" ? "primary" : "default"}
                                        onClick={() => handleNextGenerationSource("upload")}
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Stack>
                                {nextGenerationSource === "upload" ? (
                                    <Stack spacing={1.5}>
                                        <Typography variant="body2" color="text.secondary">
                                            Upload a JSON file with the design-space sample points of the next population. Accepted formats:
                                        </Typography>
                                        <Box
                                            component="ul"
                                            sx={{ m: 0, pl: 2.5, "& li": { fontSize: "0.8rem", color: "text.secondary" } }}
                                        >
                                            <li>
                                                Direct list: <code>{"[{ \"power\": 40, … }]"}</code>
                                            </li>
                                            <li>
                                                Export format of an exploration (<code>sample_points[].design_space</code>)
                                            </li>
                                        </Box>
                                        {injectedCheck?.status === "checking" ? (
                                            <Alert
                                                severity="info"
                                                icon={<CircularProgress size={16} thickness={4} />}
                                                sx={{ py: 0.5, alignItems: "center" }}
                                            >
                                                Checking {injectedFile?.name}…
                                            </Alert>
                                        ) : null}
                                        {injectedCheck?.status === "ok" ? (
                                            <Alert severity="success" sx={{ py: 0.5 }}>
                                                {injectedCheck.pointCount} sample point{injectedCheck.pointCount !== 1 ? "s" : ""} loaded
                                                {injectedFile?.name ? ` from ${injectedFile.name}` : ""}
                                                {injectedCheck.ignoredKeys.length > 0
                                                    ? `; keys outside the design space ignored: ${injectedCheck.ignoredKeys.join(", ")}`
                                                    : ""}
                                            </Alert>
                                        ) : null}
                                        {injectedCheck?.status === "refused" ? (
                                            <DesignPointsErrorAlert
                                                error={injectedCheck.error}
                                                fallback="The population file was refused."
                                            />
                                        ) : null}
                                        <input
                                            ref={injectedFileInputRef}
                                            type="file"
                                            accept=".json,application/json"
                                            style={{ display: "none" }}
                                            onChange={handleInjectedFileChange}
                                            data-testid="injected-population-file"
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<UploadFileIcon />}
                                            onClick={() => injectedFileInputRef.current?.click()}
                                            sx={{ alignSelf: "flex-start", textTransform: "none" }}
                                        >
                                            {injectedFile ? "Replace file" : "Upload JSON file"}
                                        </Button>
                                    </Stack>
                                ) : null}
                                <DesignPointsErrorAlert
                                    error={nextGenerationError}
                                    fallback="Preparing the next generation failed."
                                />
                            </Stack>
                        </Box>
                    ) : null}
                    </Box>
                </Box>
            </ExplorationPageShell>
            <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
                <NavBarBottom
                    variant="embedded"
                    prevPage={prevPage}
                    nextPage={stepRoute("resolution")}
                    hideReturnButton={true}
                    enableNextPage={!nextGenerationReady}
                    hideNextButton={isAnalysePage || explorationCompleted}
                    nextPageClickFunc={nextPageClickFunc}
                    nextLoading={nextGenerationBusy}
                >
                </NavBarBottom>
            </Box>
        </Stack>
    );
}
export default ColorExplorationColorSummary;